import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid2,
  FormControlLabel,
  IconButton,
  InputAdornment,
  MenuItem,
  Radio,
  RadioGroup,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import dayjs from 'dayjs';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import SaveAltIcon from '@mui/icons-material/SaveAlt';
import ClearIcon from '@mui/icons-material/Clear';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { FormProvider, useForm, Controller, useWatch } from 'react-hook-form';

import type { Consignee, ContainerType, Customer, DrcCode, FreightForwarder, NotifyPartner, Order, OrderType, Product, Route, ShippingCondition, SupplyPoint } from '@/types';
import {
  useConsignees,
  useContainerTypes,
  useCustomers,
  useDrcCodes,
  useFreightForwarders,
  useFreightPayers,
  useIncoterms,
  useIncotermPairs,
  useNotifyPartners,
  useOrderStatuses,
  useOrderTypes,
  useRoutes,
  useShippingConditions,
  useSupplyPoints,
} from '@/hooks/useLookup';
import { AddressFields } from '@/components/shared/AddressFields';
import { ClearProductsDialog } from '@/components/shared/ClearProductsDialog';
import { DiscardOrderDialog } from '@/components/shared/DiscardOrderDialog';
import { SelectionDialog, type SelectionDialogColumn } from '@/components/shared/SelectionDialog';
import { useOrderService } from '@/hooks/useOrderService';
import { ProductTable } from '@/pages/orders/form/ProductTable';
import { OrderDetailsCard } from '@/pages/orders/form/OrderDetailsCard';
import { getOrderTypeDescription } from '@/services/orders/orderService';
import { resolveMovementType } from '@/pages/orders/form/movementType';
import { useUser } from '@/context/UserContext';

interface OrderFormValues {
  orderNumber: string;
  creatorId: string;
  orderPlacedDate: string;
  orderType: string;
  orderTypeDescription: string;
  status: Order['status'];
  responsibleEmployee: string;
  responsibleEmployeeName: string;
  customerConfirmation: boolean;
  preCyberStatus: string;
  preCyberOrderNumber: string;
  customerId: string;
  soldToNumber: string;
  customerName: string;
  purchaseOrderNumber: string;
  contactName: string;
  contactEmail: string;
  contactPhoneNumber: string;
  shipToAddress: string;
  shipToAddressLine1: string;
  shipToAddressLine2: string;
  shipToCity: string;
  shipToState: string;
  shipToCountry: string;
  shipToPostalCode: string;
  shipToEmail: string;
  salesOrg: string;
  distributionChannel: string;
  division: string;
  drcCodeDesc: string;
  drcCodeCountry: string;
  drcCodeRegion: string;
  shipToNumber: string;
  shipToName: string;
  notifyPartnerCode: string;
  notifyPartnerName: string;
  notifyPartnerAddressLine1: string;
  notifyPartnerAddressLine2: string;
  notifyPartnerCity: string;
  notifyPartnerState: string;
  notifyPartnerCountry: string;
  notifyPartnerPostalCode: string;
  notifyPartnerEmail: string;
  criticalCustomer: boolean;
  isPickup: 'delivery' | 'pickup';
  requestedDeliveryDate: string;
  requestedPickupDate: string;
  requestedDeliveryTime: string;
  deliveryDate: string;
  shippingCondition: string;
  modeOfTransportation: string;
  supplyPointCode: string;
  supplyPointId: string;
  supplyPointName: string;
  supplyPointAddress1: string;
  supplyPointAddress2: string;
  supplyPointCity: string;
  supplyPointState: string;
  supplyPointZipCode: string;
  supplyPointCountry: string;
  routeCode: string;
  routeDescription: string;
  transitDays: string;
  plannedLoadDate: string;
  transportationPlannedDate: string;
  carrierNumber: string;
  incoterm: string;
  incotermLocation: string;
  freightForwarder: string;
  drcCode: string;
  numberOfTrucks: string;
  containerType: string;
  numberOfContainers: string;
  uniqueRCIDNumber: string;
  shipmentId: string;
  freightPayerNumber: string;
  vesselName: string;
  expectedDischargeDate: string;
  loadLocationId: string;
  overrideLoadDate: string;
  surveyInspector: string;
  vesselScreeningNumber: string;
  dischargeLocationId: string;
  consigneeCode: string;
  consigneeName: string;
  consigneeAddressLine1: string;
  consigneeAddressLine2: string;
  consigneeCity: string;
  consigneeState: string;
  consigneeCountry: string;
  consigneePostalCode: string;
  consigneeEmail: string;
  specialInstructions: string;
  haulierText: string;
  manualInvoiceNumber: string;
  products: Product[];
}

interface OrderFormProps {
  order: Order;
  onBack?: () => void;
  onSave: (order: Order, options?: { deletedProducts?: Product[] }) => Promise<void>;
  disableNotifyPartnerModal?: boolean;
}

interface CustomerAutoPopulateContext {
  requestId: number;
  shipToNumber: string;
  salesOrg: string;
  distributionChannel: string;
  division: string;
  destinationCountry: string;
}

interface NotifyPartnerSnapshot {
  code: string;
  name: string;
  addressLine1: string;
  addressLine2: string;
  city: string;
  state: string;
  country: string;
  postalCode: string;
  email: string;
}

interface ConsigneeSnapshot {
  code: string;
  name: string;
  addressLine1: string;
  addressLine2: string;
  city: string;
  state: string;
  country: string;
  postalCode: string;
  email: string;
}

const ORDER_STATUS_OPTIONS: Order['status'][] = [
  'OC-Draft',
  'Submitted',
  'Approved',
  'InProgress',
  'Completed',
  'Cancelled',
  'OnHold',
];

const normalizeDateForDateInput = (value?: string): string => {
  const rawValue = String(value ?? '').trim();
  if (!rawValue) {
    return '';
  }

  if (/^\d{4}-\d{2}-\d{2}$/.test(rawValue)) {
    return rawValue;
  }

  const isoDateCandidate = rawValue.split('T')[0];
  if (/^\d{4}-\d{2}-\d{2}$/.test(isoDateCandidate)) {
    return isoDateCandidate;
  }

  const parsedDate = new Date(rawValue);
  if (Number.isNaN(parsedDate.getTime())) {
    return '';
  }

  const year = parsedDate.getFullYear();
  const month = String(parsedDate.getMonth() + 1).padStart(2, '0');
  const day = String(parsedDate.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const formatLocalDateForInput = (value: Date): string => {
  const year = value.getFullYear();
  const month = String(value.getMonth() + 1).padStart(2, '0');
  const day = String(value.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const parseDateInputAsLocalDate = (value?: string): Date => {
  const rawValue = String(value ?? '').trim();
  if (!rawValue) {
    return new Date('');
  }

  const localDateMatch = rawValue.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (localDateMatch) {
    const year = Number(localDateMatch[1]);
    const month = Number(localDateMatch[2]);
    const day = Number(localDateMatch[3]);
    return new Date(year, month - 1, day);
  }

  return new Date(rawValue);
};

const resolveInitialOrderType = (order: Order): string => {
  const orderTypeCode = String(order.orderTypeCode ?? order.orderType ?? '').trim();
  const orderType = String(order.orderType ?? '').trim();

  if (orderTypeCode.toLowerCase() === 'sales order' || orderType.toLowerCase() === 'sales order') {
    return 'ZOR';
  }

  return orderTypeCode || orderType || 'ZOR';
};

const orderFormLineFieldTheme = createTheme({
  components: {
    MuiTextField: {
      defaultProps: {
        variant: 'standard',
        size: 'small',
      },
    },
    MuiFormControl: {
      defaultProps: {
        variant: 'standard',
        size: 'small',
      },
    },
    MuiInputBase: {
      styleOverrides: {
        input: {
          fontSize: '14px',
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          fontSize: '14px',
        },
      },
    },
    MuiCardHeader: {
      defaultProps: {
        titleTypographyProps: {
          variant: 'h6',
          sx: { fontSize: '18px', fontWeight: 700 },
        },
        subheaderTypographyProps: {
          variant: 'body2',
          sx: { fontSize: '13px', color: 'rgba(0, 0, 0, 0.6)' },
        },
      },
    },
  },
});

export const OrderForm: React.FC<OrderFormProps> = ({ order, onBack, onSave, disableNotifyPartnerModal = false }) => {
  const { getBusinessUnit } = useUser();
  const { calculateDeliveryDate, calculatePlanningDates } = useOrderService();
  const formId = 'order-details-form';
  const normalizeLookupCode = (value?: string) => String(value ?? '').split(' - ')[0].trim();
  const getSupplyPointValue = (supplyPoint: SupplyPoint, keys: string[]) => {
    const record = supplyPoint as SupplyPoint & Record<string, unknown>;

    for (const key of keys) {
      const value = record[key];
      if (typeof value === 'string') {
        const trimmed = value.trim();
        if (trimmed) {
          return trimmed;
        }
        continue;
      }

      if (typeof value === 'number' && Number.isFinite(value)) {
        return String(value);
      }

      if (value != null) {
        const stringValue = String(value).trim();
        if (stringValue) {
          return stringValue;
        }
      }
    }

    return '';
  };
  const composeLookupDisplay = (code?: string, description?: string) => {
    const rawCode = String(code ?? '').trim();
    const normalizedCode = normalizeLookupCode(rawCode);
    const normalizedDescription = String(description ?? '').trim();
    if (normalizedCode && normalizedDescription) {
      return `${normalizedCode} - ${normalizedDescription}`;
    }
    if (rawCode.includes(' - ')) {
      return rawCode;
    }
    return normalizedCode;
  };
  const getIncotermLookupCode = (term: unknown) => {
    const option = term as Record<string, unknown>;
    return String(option.code ?? option.inco1 ?? option.incoterms1 ?? option.incoterm ?? '').trim();
  };
  const getShippingConditionLookupCode = (condition: unknown) => {
    const option = condition as Record<string, unknown>;
    return String(option.shippingConditionId ?? option.code ?? option.shippingCondition ?? '').trim();
  };
  const getShippingConditionLookupDescription = (condition: unknown) => {
    const option = condition as Record<string, unknown>;
    return String(option.shippingConditionDescription ?? option.description ?? '').trim();
  };
  const getIncotermLookupDescription = (term: unknown) => {
    const option = term as Record<string, unknown>;
    return String(
      option.description
        ?? option.incoterms1Description
        ?? option.incotermsDescription
        ?? option.inco1Description
        ?? option.incoDescription
        ?? option.incotermDescription
        ?? option.inco1Desc
        ?? option.incoterms1Desc
        ?? '',
    ).trim();
  };
  const shipToAddress = [
    order.shipToAddress?.name,
    order.shipToAddress?.street,
    order.shipToAddress?.city,
    order.shipToAddress?.state,
    order.shipToAddress?.postalCode,
    order.shipToAddress?.country,
  ].filter(Boolean).join(', ');

  const methods = useForm<OrderFormValues>({
    mode: 'onChange',
    reValidateMode: 'onChange',
    defaultValues: {
      orderNumber: order.orderNumber ?? order.orderId,
      creatorId: order.creatorId ?? '',
      orderPlacedDate: order.orderPlacedDate ?? '',
      orderType: resolveInitialOrderType(order),
      orderTypeDescription: order.orderTypeDescription ?? order.orderType ?? '',
      status: order.status,
      responsibleEmployee: order.responsibleEmployee ?? '',
      responsibleEmployeeName: order.responsibleEmployeeName ?? '',
      customerConfirmation: order.customerConfirmation ?? false,
      preCyberStatus: order.preCyberStatus ?? order.preCyberOrderStatus ?? '',
      preCyberOrderNumber: order.preCyberOrderNumber ?? '',
      customerId: order.customerId ?? '',
      soldToNumber: order.soldToNumber ?? order.customerId ?? '',
      customerName: order.customerName ?? '',
      purchaseOrderNumber: order.purchaseOrderNumber ?? '',
      contactName: order.contactName ?? '',
      contactEmail: order.contactEmail ?? '',
      contactPhoneNumber: order.contactPhoneNumber ?? '',
      shipToAddress,
      shipToAddressLine1: order.shipToAddressLine1 ?? order.shipToAddress?.line1 ?? order.shipToAddress?.street ?? '',
      shipToAddressLine2: order.shipToAddressLine2 ?? order.shipToAddress?.line2 ?? '',
      shipToCity: order.shipToCity ?? order.shipToAddress?.city ?? '',
      shipToState: order.shipToState ?? order.shipToAddress?.state ?? '',
      shipToCountry: order.shipToCountry ?? order.shipToAddress?.country ?? '',
      shipToPostalCode: order.shipToPostalCode ?? order.shipToAddress?.postalCode ?? '',
      shipToEmail: '',
      salesOrg: order.salesOrg ?? order.sellingSalesOrg ?? '',
      distributionChannel: order.distributionChannel ?? '',
      division: order.division ?? '',
      drcCodeDesc: order.drcCodeDesc ?? '',
      drcCodeCountry: order.drcCodeCountry ?? '',
      drcCodeRegion: order.drcCodeRegion ?? '',
      shipToNumber: order.shipToNumber ?? order.shipToPlantCode ?? '',
      shipToName: order.shipToName ?? order.shipToPlantName ?? '',
      notifyPartnerCode: order.notifyPartnerCode ?? '',
      notifyPartnerName: order.notifyPartnerName ?? '',
      notifyPartnerAddressLine1: (order.notifyPartner?.address as Partial<Record<'addressLine1' | 'street', string>> | undefined)?.addressLine1
        ?? order.notifyPartner?.address?.street
        ?? '',
      notifyPartnerAddressLine2: (order.notifyPartner?.address as Partial<Record<'addressLine2' | 'line2', string>> | undefined)?.addressLine2
        ?? order.notifyPartner?.address?.line2
        ?? order.notifyPartner?.address2
        ?? '',
      notifyPartnerCity: order.notifyPartner?.address?.city ?? '',
      notifyPartnerState: order.notifyPartner?.address?.state ?? '',
      notifyPartnerCountry: order.notifyPartner?.address?.country ?? '',
      notifyPartnerPostalCode: (order.notifyPartner?.address as Partial<Record<'zipCode' | 'postalCode', string>> | undefined)?.zipCode
        ?? order.notifyPartner?.address?.postalCode
        ?? '',
      notifyPartnerEmail: (order.notifyPartner?.address as Partial<Record<'eMail', string>> | undefined)?.eMail
        ?? order.notifyPartner?.eMail
        ?? '',
      criticalCustomer: order.criticalCustomer ?? false,
      isPickup: order.isPickup === true ? 'pickup' : 'delivery',
      requestedDeliveryDate: order.isPickup ? '' : normalizeDateForDateInput(order.requestedDeliveryDate),
      requestedPickupDate: order.isPickup ? normalizeDateForDateInput(order.requestedPickupDate ?? order.requestedDeliveryDate) : '',
      requestedDeliveryTime: order.requestedDeliveryTime ?? '',
      deliveryDate: normalizeDateForDateInput(order.deliveryDate),
      shippingCondition: composeLookupDisplay(order.shippingCondition, order.shippingConditionDescription),
      modeOfTransportation: order.modeOfTransportation ?? '',
      supplyPointCode: order.supplyPointCode ?? '',
      supplyPointId: (order as Order & { supplyPointId?: string }).supplyPointId ?? order.supplyPointCode ?? '',
      supplyPointName: order.supplyPointName ?? '',
      supplyPointAddress1: order.supplyPointAddress1 ?? '',
      supplyPointAddress2: order.supplyPointAddress2 ?? '',
      supplyPointCity: order.supplyPointCity ?? '',
      supplyPointState: order.supplyPointState ?? '',
      supplyPointZipCode: order.supplyPointZipCode ?? '',
      supplyPointCountry: order.supplyPointCountry ?? '',
      routeCode: order.routeCode ?? '',
      routeDescription: order.routeDescription ?? '',
      transitDays: order.transitDays != null ? String(order.transitDays) : '',
      plannedLoadDate: normalizeDateForDateInput(order.plannedLoadDate),
      transportationPlannedDate: normalizeDateForDateInput(order.transportationPlannedDate),
      carrierNumber: order.carrierNumber ?? '',
      incoterm: composeLookupDisplay(order.incoterm, order.incotermDescription),
      incotermLocation: order.incotermLocation ?? '',
      freightForwarder: order.freightForwarderId ?? order.freightForwarder ?? '',
      drcCode: order.drcCodeId ?? order.drcCode ?? '',
      numberOfTrucks: order.numberOfTrucks != null ? String(order.numberOfTrucks) : '1',
      containerType: order.containerType ?? '',
      numberOfContainers: order.numberOfContainers != null ? String(order.numberOfContainers) : '1',
      uniqueRCIDNumber: order.uniqueRCIDNumber ?? '',
      shipmentId: order.shipmentId ?? '',
      freightPayerNumber: order.freightPayerNumber ?? '',
      vesselName: order.vesselName ?? '',
      expectedDischargeDate: normalizeDateForDateInput(order.expectedDischargeDate),
      loadLocationId: order.loadLocationId ?? '',
      overrideLoadDate: normalizeDateForDateInput(order.overrideLoadDate),
      surveyInspector: order.surveyInspector ?? '',
      vesselScreeningNumber: order.vesselScreeningNumber ?? '',
      dischargeLocationId: order.dischargeLocationId ?? '',
      consigneeCode: order.consigneeCode ?? order.consigneePartner ?? '',
      consigneeName: order.consigneeName ?? '',
      consigneeAddressLine1: order.consigneeAddressLine1 ?? '',
      consigneeAddressLine2: order.consigneeAddressLine2 ?? '',
      consigneeCity: order.consigneeCity ?? '',
      consigneeState: order.consigneeState ?? '',
      consigneeCountry: order.consigneeCountry ?? '',
      consigneePostalCode: order.consigneePostalCode ?? '',
      consigneeEmail: order.consigneeEmail ?? '',
      specialInstructions: order.specialInstructions ?? '',
      haulierText: order.haulierText ?? '',
      manualInvoiceNumber: order.manualInvoiceNumber ?? '',
      products: order.products ?? [],
    },
  });

  const [isSaving, setIsSaving] = useState(false);
  const [supplyPointDialogOpen, setSupplyPointDialogOpen] = useState(false);
  const [routeDialogOpen, setRouteDialogOpen] = useState(false);
  const [shippingConditionDialogOpen, setShippingConditionDialogOpen] = useState(false);
  const [discardDialogOpen, setDiscardDialogOpen] = useState(false);
  const [clearProductsDialogOpen, setClearProductsDialogOpen] = useState(false);
  const [saveDisabledAfterSave, setSaveDisabledAfterSave] = useState(false);
  const [customerDialogOpen, setCustomerDialogOpen] = useState(false);
  const [freightForwarderDialogOpen, setFreightForwarderDialogOpen] = useState(false);
  const [notifyPartnerDialogOpen, setNotifyPartnerDialogOpen] = useState(false);
  const [consigneeDialogOpen, setConsigneeDialogOpen] = useState(false);
  const [drcCodeDialogOpen, setDrcCodeDialogOpen] = useState(false);
  const [customerSearchInput, setCustomerSearchInput] = useState('');
  const [shippingConditionSearchInput, setShippingConditionSearchInput] = useState('');
  const [customerSearchTerm, setCustomerSearchTerm] = useState('');
  const [customerPage, setCustomerPage] = useState(0);
  const [customerRowsPerPage, setCustomerRowsPerPage] = useState(5);
  const [freightForwarderSearchInput, setFreightForwarderSearchInput] = useState('');
  const [freightForwarderSearchTerm, setFreightForwarderSearchTerm] = useState('');
  const [freightForwarderPage, setFreightForwarderPage] = useState(0);
  const [freightForwarderRowsPerPage, setFreightForwarderRowsPerPage] = useState(5);
  const [notifyPartnerSearchInput, setNotifyPartnerSearchInput] = useState('');
  const [notifyPartnerSearchTerm, setNotifyPartnerSearchTerm] = useState('');
  const [notifyPartnerPage, setNotifyPartnerPage] = useState(0);
  const [notifyPartnerRowsPerPage, setNotifyPartnerRowsPerPage] = useState(5);
  const [consigneeSearchInput, setConsigneeSearchInput] = useState('');
  const [consigneeSearchTerm, setConsigneeSearchTerm] = useState('');
  const [consigneePage, setConsigneePage] = useState(0);
  const [consigneeRowsPerPage, setConsigneeRowsPerPage] = useState(5);
  const [drcCodeSearchInput, setDrcCodeSearchInput] = useState('');
  const [drcCodeSearchTerm, setDrcCodeSearchTerm] = useState('');
  const [drcCodePage, setDrcCodePage] = useState(0);
  const [drcCodeRowsPerPage, setDrcCodeRowsPerPage] = useState(5);
  const [supplyPointSearchInput, setSupplyPointSearchInput] = useState('');
  const [supplyPointSearchTerm, setSupplyPointSearchTerm] = useState('');
  const [supplyPointPage, setSupplyPointPage] = useState(0);
  const [supplyPointRowsPerPage, setSupplyPointRowsPerPage] = useState(5);
  const [routeSearchInput, setRouteSearchInput] = useState('');
  const [routeSearchTerm, setRouteSearchTerm] = useState('');
  const [routePage, setRoutePage] = useState(0);
  const [routeRowsPerPage, setRouteRowsPerPage] = useState(5);
  const [routeDetailsError, setRouteDetailsError] = useState(false);
  const [skipDiscardPrompt, setSkipDiscardPrompt] = useState(false);
  const [containerTypeDialogOpen, setContainerTypeDialogOpen] = useState(false);
  const [selectedSupplyPointTimeZone, setSelectedSupplyPointTimeZone] = useState(order.supplyPointTimeZone ?? '');
  const [suggestedRequestedDate, setSuggestedRequestedDate] = useState<Date | null>(null);
  const [deletedProducts, setDeletedProducts] = useState<Product[]>([]);
  const [customerAutoPopulateContext, setCustomerAutoPopulateContext] = useState<CustomerAutoPopulateContext | null>(null);
  const planningDatesRequestIdRef = useRef(0);
  const suggestedRequestedDateRequestIdRef = useRef(0);
  const previousMovementTypeRef = useRef('');
  const previousIsPickupRef = useRef<'delivery' | 'pickup' | null>(null);
  const previousIncotermCodeRef = useRef(normalizeLookupCode(String(methods.getValues('incoterm') ?? '')));
  const notifyPartnerSnapshotRef = useRef<NotifyPartnerSnapshot>({
    code: String(methods.getValues('notifyPartnerCode') ?? '').trim(),
    name: String(methods.getValues('notifyPartnerName') ?? '').trim(),
    addressLine1: String(methods.getValues('notifyPartnerAddressLine1') ?? '').trim(),
    addressLine2: String(methods.getValues('notifyPartnerAddressLine2') ?? '').trim(),
    city: String(methods.getValues('notifyPartnerCity') ?? '').trim(),
    state: String(methods.getValues('notifyPartnerState') ?? '').trim(),
    country: String(methods.getValues('notifyPartnerCountry') ?? '').trim(),
    postalCode: String(methods.getValues('notifyPartnerPostalCode') ?? '').trim(),
    email: String(methods.getValues('notifyPartnerEmail') ?? '').trim(),
  });
  const consigneeSnapshotRef = useRef<ConsigneeSnapshot>({
    code: String(methods.getValues('consigneeCode') ?? '').trim(),
    name: String(methods.getValues('consigneeName') ?? '').trim(),
    addressLine1: String(methods.getValues('consigneeAddressLine1') ?? '').trim(),
    addressLine2: String(methods.getValues('consigneeAddressLine2') ?? '').trim(),
    city: String(methods.getValues('consigneeCity') ?? '').trim(),
    state: String(methods.getValues('consigneeState') ?? '').trim(),
    country: String(methods.getValues('consigneeCountry') ?? '').trim(),
    postalCode: String(methods.getValues('consigneePostalCode') ?? '').trim(),
    email: String(methods.getValues('consigneeEmail') ?? '').trim(),
  });
  const persistedProductItemNumbersRef = useRef<Set<string>>(new Set((order.products ?? []).map((product) => String(product.itemNumber ?? '').trim()).filter(Boolean)));
  const watchedProducts = methods.watch('products');
  const products = watchedProducts;
  const currentShippingConditionCode = String(methods.watch('shippingCondition') ?? '').trim().split(' ')[0] || null;
  const currentOrderTypeCode = String(methods.watch('orderType') ?? '').trim() || null;
  const currentSupplyPointId = methods.watch('supplyPointId');
  const currentSupplyPointCode = methods.watch('supplyPointCode');
  const currentSupplyPointCountry = String(methods.watch('supplyPointCountry') ?? '').trim() || null;
  const currentShipToNumber = methods.watch('shipToNumber');
  const currentShipToCountry = String(methods.watch('shipToCountry') ?? '').trim() || null;
  const currentSalesOrg = String(methods.watch('salesOrg') ?? '').trim() || null;
  const currentDistributionChannel = String(methods.watch('distributionChannel') ?? '').trim() || null;
  const currentDivision = String(methods.watch('division') ?? '').trim() || null;

  const queueDeletedPersistedProducts = (productsToDelete: Product[]) => {
    const persistedProductsToDelete = productsToDelete.filter((product) => {
      const itemNumber = String(product.itemNumber ?? '').trim();
      return itemNumber && persistedProductItemNumbersRef.current.has(itemNumber);
    });

    if (persistedProductsToDelete.length === 0) {
      return;
    }

    setDeletedProducts((currentDeletedProducts) => {
      const deletedItemNumbers = new Set(
        currentDeletedProducts
          .map((product) => String(product.itemNumber ?? '').trim())
          .filter(Boolean),
      );

      const uniqueProductsToDelete = persistedProductsToDelete.filter((product) => {
        const itemNumber = String(product.itemNumber ?? '').trim();
        return !deletedItemNumbers.has(itemNumber);
      });

      if (uniqueProductsToDelete.length === 0) {
        return currentDeletedProducts;
      }

      return [...currentDeletedProducts, ...uniqueProductsToDelete];
    });
  };

  const supplyPointsQuery = useSupplyPoints({
    enabled: supplyPointDialogOpen,
    searchTerm: supplyPointSearchTerm.trim(),
    shippingCondition: currentShippingConditionCode,
    pageNumber: supplyPointPage + 1,
    linesPerPage: supplyPointRowsPerPage,
  });
  
  const autoLoadRoutesQuery = useRoutes({
    enabled: !!currentSupplyPointCode && !!currentShippingConditionCode && !routeDialogOpen,
    searchTerm: '',
    shippingCondition: currentShippingConditionCode,
    shipTo: currentShipToNumber,
    supplyPoint: currentSupplyPointCode,
    materialCode: null,
    pageNumber: 1,
    linesPerPage: 5,
  });
  const customersQuery = useCustomers(customerSearchTerm.trim(), customerDialogOpen, customerPage + 1, customerRowsPerPage);
  const freightForwardersQuery = useFreightForwarders({
    enabled: freightForwarderDialogOpen,
    division: currentDivision,
    orderType: currentOrderTypeCode,
    shippingCondition: currentShippingConditionCode,
    supplyPoint: String(currentSupplyPointId ?? '').trim() || String(currentSupplyPointCode ?? '').trim() || null,
    sourcePlant: String(currentShipToNumber ?? '').trim() || null,
    originCountry: currentSupplyPointCountry,
    destinationCountry: currentShipToCountry,
    pageNumber: freightForwarderPage + 1,
    linesPerPage: freightForwarderRowsPerPage,
  });
  const notifyPartnerLookupQuery = useNotifyPartners({
    enabled: notifyPartnerDialogOpen,
    customerNumber: currentShipToNumber,
    salesOrg: currentSalesOrg,
    distributionChannel: currentDistributionChannel,
    division: currentDivision,
    searchTerm: notifyPartnerSearchTerm.trim(),
    pageNumber: notifyPartnerPage + 1,
    linesPerPage: notifyPartnerRowsPerPage,
  });
  const consigneeLookupQuery = useConsignees({
    enabled: consigneeDialogOpen,
    customerNumber: currentShipToNumber,
    salesOrg: currentSalesOrg,
    distributionChannel: currentDistributionChannel,
    division: currentDivision,
    searchTerm: consigneeSearchTerm.trim(),
    pageNumber: consigneePage + 1,
    linesPerPage: consigneeRowsPerPage,
  });
  const drcCodesQuery = useDrcCodes({
    enabled: drcCodeDialogOpen,
    customerNumber: currentShipToNumber,
    salesOrg: currentSalesOrg,
    distributionChannel: currentDistributionChannel,
    division: currentDivision,
    orderType: currentOrderTypeCode,
    shippingCondition: currentShippingConditionCode,
    searchTerm: drcCodeSearchTerm.trim(),
    pageNumber: drcCodePage + 1,
    linesPerPage: drcCodeRowsPerPage,
  });
  const autoPopulateDrcCodesQuery = useDrcCodes({
    enabled: customerAutoPopulateContext != null && getBusinessUnit() === 'AMP',
    customerNumber: customerAutoPopulateContext?.shipToNumber,
    salesOrg: customerAutoPopulateContext?.salesOrg,
    distributionChannel: customerAutoPopulateContext?.distributionChannel,
    division: customerAutoPopulateContext?.division,
    orderType: currentOrderTypeCode,
    shippingCondition: currentShippingConditionCode,
    pageNumber: 1,
    linesPerPage: 5,
  });
  const routesQuery = useRoutes({
    enabled: routeDialogOpen,
    searchTerm: routeSearchTerm.trim(),
    shippingCondition: currentShippingConditionCode,
    shipTo: currentShipToNumber,
    supplyPoint: currentSupplyPointCode,
    materialCode: null,
    pageNumber: routePage + 1,
    linesPerPage: routeRowsPerPage,
  });
  const shippingConditionsQuery = useShippingConditions();
  const containerTypesQuery = useContainerTypes();
  const orderStatusesQuery = useOrderStatuses();
  const incotermsQuery = useIncoterms();
  const incotermPairsQuery = useIncotermPairs({
    enabled: !!currentShipToNumber && !!currentSalesOrg && !!currentDistributionChannel && !!currentDivision,
    customerNumber: currentShipToNumber,
    salesOrg: currentSalesOrg,
    distributionChannel: currentDistributionChannel,
    division: currentDivision,
  });
  const notifyPartnersQuery = useNotifyPartners({
    enabled: customerAutoPopulateContext != null,
    customerNumber: customerAutoPopulateContext?.shipToNumber,
    salesOrg: customerAutoPopulateContext?.salesOrg,
    distributionChannel: customerAutoPopulateContext?.distributionChannel,
    division: customerAutoPopulateContext?.division,
    linesPerPage: 5,
    pageNumber: 1,
  });
  const existingConsigneeHydrationCode = String(order.consigneeCode ?? order.consigneePartner ?? '').trim();
  const existingConsigneeHydrationQuery = useConsignees({
    enabled: Boolean(
      existingConsigneeHydrationCode
      && (
        !String(order.consigneeName ?? '').trim()
        || !String(order.consigneeAddressLine1 ?? '').trim()
        || !String(order.consigneeAddressLine2 ?? '').trim()
        || !String(order.consigneeCity ?? '').trim()
        || !String(order.consigneeState ?? '').trim()
        || !String(order.consigneeCountry ?? '').trim()
        || !String(order.consigneePostalCode ?? '').trim()
        || !String(order.consigneeEmail ?? '').trim()
      )
      && currentShipToNumber
      && currentSalesOrg
      && currentDistributionChannel
      && currentDivision,
    ),
    searchTerm: existingConsigneeHydrationCode,
    customerNumber: currentShipToNumber,
    salesOrg: currentSalesOrg,
    distributionChannel: currentDistributionChannel,
    division: currentDivision,
    linesPerPage: 1,
    pageNumber: 1,
  });
  const freightPayersQuery = useFreightPayers({
    enabled: customerAutoPopulateContext != null,
    customerNumber: customerAutoPopulateContext?.shipToNumber,
    salesOrg: customerAutoPopulateContext?.salesOrg,
    distributionChannel: customerAutoPopulateContext?.distributionChannel,
    division: customerAutoPopulateContext?.division,
  });
  const orderTypesQuery = useOrderTypes();

  const orderTypeOptions = useMemo(() => {
    const options = [...(orderTypesQuery.data ?? [])];
    const currentCode = resolveInitialOrderType(order);

    if (currentCode && !options.some((option) => option.orderTypeCode.toUpperCase() === currentCode.toUpperCase())) {
      options.push({
        orderTypeCode: currentCode,
        orderTypeDescription: getOrderTypeDescription(currentCode) ?? currentCode,
      });
    }

    return options.map((option) => ({
      ...option,
      orderTypeDescription: getOrderTypeDescription(option.orderTypeCode) ?? option.orderTypeDescription,
    }));
  }, [order, orderTypesQuery.data]);

  const orderStatusOptions = useMemo(() => {
    const baseOptions = orderStatusesQuery.data ?? ORDER_STATUS_OPTIONS;
    const normalizedOptions = baseOptions
      .map((option) => String(option ?? '').trim())
      .map((option) => (option === 'Draft' ? 'OC-Draft' : option))
      .filter((option): option is string => option.length > 0);

    if (!normalizedOptions.includes('OC-Draft')) {
      normalizedOptions.unshift('OC-Draft');
    }

    return Array.from(new Set(normalizedOptions));
  }, [orderStatusesQuery.data]);

  useEffect(() => {
    const currentValue = String(methods.getValues('shippingCondition') ?? '').trim();
    if (!currentValue) {
      return;
    }

    const normalizedCode = normalizeLookupCode(currentValue);
    const matched = (shippingConditionsQuery.data ?? []).find((condition) =>
      normalizeLookupCode(String(condition.code ?? '')) === normalizedCode,
    );

    if (!matched) {
      return;
    }

    const displayValue = composeLookupDisplay(String(matched.code ?? ''), String(matched.description ?? ''));
    if (displayValue && displayValue !== currentValue) {
      methods.setValue('shippingCondition', displayValue, { shouldDirty: false });
    }
  }, [shippingConditionsQuery.data, methods]);

  useEffect(() => {
    const currentValue = String(methods.getValues('incoterm') ?? '').trim();
    if (!currentValue) {
      return;
    }

    const normalizedCode = normalizeLookupCode(currentValue);
    const matched = (incotermsQuery.data ?? []).find((term) => normalizeLookupCode(getIncotermLookupCode(term)) === normalizedCode);

    if (!matched) {
      return;
    }

    const displayValue = composeLookupDisplay(getIncotermLookupCode(matched), getIncotermLookupDescription(matched));
    if (displayValue && displayValue !== currentValue) {
      methods.setValue('incoterm', displayValue, { shouldDirty: false });
    }
  }, [incotermsQuery.data, methods]);

  useEffect(() => {
    persistedProductItemNumbersRef.current = new Set(
      (order.products ?? [])
        .map((product) => String(product.itemNumber ?? '').trim())
        .filter(Boolean),
    );
    setDeletedProducts([]);
    setCustomerAutoPopulateContext(null);
    setSuggestedRequestedDate(null);
  }, [order.products]);

  const getNotifyPartnerSnapshot = (): NotifyPartnerSnapshot => ({
    code: String(methods.getValues('notifyPartnerCode') ?? '').trim(),
    name: String(methods.getValues('notifyPartnerName') ?? '').trim(),
    addressLine1: String(methods.getValues('notifyPartnerAddressLine1') ?? '').trim(),
    addressLine2: String(methods.getValues('notifyPartnerAddressLine2') ?? '').trim(),
    city: String(methods.getValues('notifyPartnerCity') ?? '').trim(),
    state: String(methods.getValues('notifyPartnerState') ?? '').trim(),
    country: String(methods.getValues('notifyPartnerCountry') ?? '').trim(),
    postalCode: String(methods.getValues('notifyPartnerPostalCode') ?? '').trim(),
    email: String(methods.getValues('notifyPartnerEmail') ?? '').trim(),
  });

  const getConsigneeSnapshot = (): ConsigneeSnapshot => ({
    code: String(methods.getValues('consigneeCode') ?? '').trim(),
    name: String(methods.getValues('consigneeName') ?? '').trim(),
    addressLine1: String(methods.getValues('consigneeAddressLine1') ?? '').trim(),
    addressLine2: String(methods.getValues('consigneeAddressLine2') ?? '').trim(),
    city: String(methods.getValues('consigneeCity') ?? '').trim(),
    state: String(methods.getValues('consigneeState') ?? '').trim(),
    country: String(methods.getValues('consigneeCountry') ?? '').trim(),
    postalCode: String(methods.getValues('consigneePostalCode') ?? '').trim(),
    email: String(methods.getValues('consigneeEmail') ?? '').trim(),
  });

  const setNotifyPartnerFields = (notifyPartner: Partial<NotifyPartnerSnapshot>, markDirty = true) => {
    const options = { shouldDirty: markDirty, shouldValidate: true, shouldTouch: markDirty };
    const nextSnapshot: NotifyPartnerSnapshot = {
      code: String(notifyPartner.code ?? '').trim(),
      name: String(notifyPartner.name ?? '').trim(),
      addressLine1: String(notifyPartner.addressLine1 ?? '').trim(),
      addressLine2: String(notifyPartner.addressLine2 ?? '').trim(),
      city: String(notifyPartner.city ?? '').trim(),
      state: String(notifyPartner.state ?? '').trim(),
      country: String(notifyPartner.country ?? '').trim(),
      postalCode: String(notifyPartner.postalCode ?? '').trim(),
      email: String(notifyPartner.email ?? '').trim(),
    };

    methods.setValue('notifyPartnerCode', nextSnapshot.code, options);
    methods.setValue('notifyPartnerName', nextSnapshot.name, options);
    methods.setValue('notifyPartnerAddressLine1', nextSnapshot.addressLine1, options);
    methods.setValue('notifyPartnerAddressLine2', nextSnapshot.addressLine2, options);
    methods.setValue('notifyPartnerCity', nextSnapshot.city, options);
    methods.setValue('notifyPartnerState', nextSnapshot.state, options);
    methods.setValue('notifyPartnerCountry', nextSnapshot.country, options);
    methods.setValue('notifyPartnerPostalCode', nextSnapshot.postalCode, options);
    methods.setValue('notifyPartnerEmail', nextSnapshot.email, options);
    notifyPartnerSnapshotRef.current = nextSnapshot;
  };

  const setConsigneeFields = (consignee: Partial<ConsigneeSnapshot>, markDirty = true) => {
    const options = { shouldDirty: markDirty, shouldValidate: true, shouldTouch: markDirty };
    const nextSnapshot: ConsigneeSnapshot = {
      code: String(consignee.code ?? '').trim(),
      name: String(consignee.name ?? '').trim(),
      addressLine1: String(consignee.addressLine1 ?? '').trim(),
      addressLine2: String(consignee.addressLine2 ?? '').trim(),
      city: String(consignee.city ?? '').trim(),
      state: String(consignee.state ?? '').trim(),
      country: String(consignee.country ?? '').trim(),
      postalCode: String(consignee.postalCode ?? '').trim(),
      email: String(consignee.email ?? '').trim(),
    };

    methods.setValue('consigneeCode', nextSnapshot.code, options);
    methods.setValue('consigneeName', nextSnapshot.name, options);
    methods.setValue('consigneeAddressLine1', nextSnapshot.addressLine1, options);
    methods.setValue('consigneeAddressLine2', nextSnapshot.addressLine2, options);
    methods.setValue('consigneeCity', nextSnapshot.city, options);
    methods.setValue('consigneeState', nextSnapshot.state, options);
    methods.setValue('consigneeCountry', nextSnapshot.country, options);
    methods.setValue('consigneePostalCode', nextSnapshot.postalCode, options);
    methods.setValue('consigneeEmail', nextSnapshot.email, options);
    consigneeSnapshotRef.current = nextSnapshot;
  };

  const clearNotifyPartnerFields = (markDirty = true) => {
    setNotifyPartnerFields({}, markDirty);
  };

  useEffect(() => {
    if (disableNotifyPartnerModal && notifyPartnerDialogOpen) {
      setNotifyPartnerDialogOpen(false);
    }
  }, [disableNotifyPartnerModal, notifyPartnerDialogOpen]);

  const clearConsigneeFields = (markDirty = true) => {
    setConsigneeFields({}, markDirty);
  };

  useEffect(() => {
    if (!customerAutoPopulateContext) {
      return;
    }

    const incotermPair = incotermPairsQuery.data?.[0];
    if (!incotermPair) {
      return;
    }

    const incotermCode = String(incotermPair.inco1 ?? '').trim();
    if (incotermCode) {
      const matchedIncoterm = (incotermsQuery.data ?? []).find((term) => (
        normalizeLookupCode(getIncotermLookupCode(term)) === normalizeLookupCode(incotermCode)
      ));

      methods.setValue(
        'incoterm',
        composeLookupDisplay(
          incotermCode,
          matchedIncoterm ? getIncotermLookupDescription(matchedIncoterm) : undefined,
        ),
        { shouldDirty: true, shouldValidate: true, shouldTouch: true },
      );
    }

    methods.setValue('incotermLocation', String(incotermPair.inco2 ?? '').trim(), {
      shouldDirty: true,
      shouldValidate: true,
      shouldTouch: true,
    });
  }, [customerAutoPopulateContext?.requestId, incotermPairsQuery.data, incotermsQuery.data, methods]);

  useEffect(() => {
    if (!customerAutoPopulateContext) {
      return;
    }

    const notifyPartners = notifyPartnersQuery.data?.data ?? [];
    if (notifyPartners.length !== 1) {
      clearNotifyPartnerFields();
      return;
    }

    const notifyPartner = notifyPartners[0] as unknown as Record<string, unknown>;
    setNotifyPartnerFields({
      code: String(notifyPartner.notifyPartnerNumber ?? notifyPartner.partnerId ?? '').trim(),
      name: String(notifyPartner.name ?? notifyPartner.partnerName ?? '').trim(),
      addressLine1: String(notifyPartner.address1 ?? '').trim(),
      addressLine2: String(notifyPartner.address2 ?? '').trim(),
      city: String(notifyPartner.city ?? '').trim(),
      state: String(notifyPartner.state ?? '').trim(),
      country: String(notifyPartner.country ?? '').trim(),
      postalCode: String(notifyPartner.zipCode ?? '').trim(),
      email: String(notifyPartner.eMail ?? '').trim(),
    });
  }, [customerAutoPopulateContext?.requestId, notifyPartnersQuery.data, methods]);

  useEffect(() => {
    if (!existingConsigneeHydrationCode) {
      return;
    }

    const consignee = existingConsigneeHydrationQuery.data?.data?.[0];
    if (!consignee) {
      return;
    }

    const currentSnapshot = getConsigneeSnapshot();
    const hydratedSnapshot = {
      code: currentSnapshot.code || getConsigneeValue(consignee, 'consigneeCode', 'consigneeNumber'),
      name: currentSnapshot.name || getConsigneeValue(consignee, 'consigneeName', 'name'),
      addressLine1: currentSnapshot.addressLine1 || getConsigneeValue(consignee, 'addressLine1', 'address1'),
      addressLine2: currentSnapshot.addressLine2 || getConsigneeValue(consignee, 'addressLine2', 'address2', 'AddressLine2', 'Address2', 'line2', 'address.line2'),
      city: currentSnapshot.city || getConsigneeValue(consignee, 'city'),
      state: currentSnapshot.state || getConsigneeValue(consignee, 'state'),
      country: currentSnapshot.country || getConsigneeValue(consignee, 'country'),
      postalCode: currentSnapshot.postalCode || getConsigneeValue(consignee, 'postalCode', 'zipCode'),
      email: currentSnapshot.email || getConsigneeValue(consignee, 'email', 'eMail'),
    };

    const hasMissingFields = currentSnapshot.name !== hydratedSnapshot.name
      || currentSnapshot.addressLine1 !== hydratedSnapshot.addressLine1
      || currentSnapshot.addressLine2 !== hydratedSnapshot.addressLine2
      || currentSnapshot.city !== hydratedSnapshot.city
      || currentSnapshot.state !== hydratedSnapshot.state
      || currentSnapshot.country !== hydratedSnapshot.country
      || currentSnapshot.postalCode !== hydratedSnapshot.postalCode
      || currentSnapshot.email !== hydratedSnapshot.email;

    if (!hasMissingFields) {
      return;
    }

    setConsigneeFields(hydratedSnapshot, false);
  }, [existingConsigneeHydrationCode, existingConsigneeHydrationQuery.data]);

  useEffect(() => {
    if (!customerAutoPopulateContext) {
      return;
    }

    const freightPayer = freightPayersQuery.data?.[0];
    methods.setValue('freightPayerNumber', String(freightPayer?.freightPayerNumber ?? '').trim(), {
      shouldDirty: true,
      shouldValidate: true,
      shouldTouch: true,
    });
  }, [customerAutoPopulateContext?.requestId, freightPayersQuery.data, methods]);

  useEffect(() => {
    if (!customerAutoPopulateContext || getBusinessUnit() !== 'AMP') {
      return;
    }

    const drcCode = autoPopulateDrcCodesQuery.data?.data?.[0];
    methods.setValue('drcCode', String(drcCode?.drcCodeNumber ?? '').trim(), {
      shouldDirty: true,
      shouldValidate: true,
      shouldTouch: true,
    });
    methods.setValue('drcCodeDesc', String(drcCode?.drcCodeDesc ?? '').trim(), {
      shouldDirty: true,
      shouldValidate: true,
      shouldTouch: true,
    });
    methods.setValue('drcCodeCountry', String(drcCode?.drcCodeCountry ?? '').trim(), {
      shouldDirty: true,
      shouldValidate: true,
      shouldTouch: true,
    });
    methods.setValue('drcCodeRegion', String(drcCode?.drcCodeRegion ?? '').trim(), {
      shouldDirty: true,
      shouldValidate: true,
      shouldTouch: true,
    });
  }, [autoPopulateDrcCodesQuery.data, customerAutoPopulateContext?.requestId, getBusinessUnit, methods]);

  const openCustomerDialog = () => {
    setCustomerSearchInput('');
    setCustomerSearchTerm('');
    setCustomerPage(0);
    setCustomerRowsPerPage(5);
    setCustomerDialogOpen(true);
  };

  const handleOpenCustomerDialog = () => {
    const hasProducts = products && products.length > 0;
    if (hasProducts) {
      setClearProductsDialogOpen(true);
    } else {
      openCustomerDialog();
    }
  };

  const handleContinueClearProducts = () => {
    setClearProductsDialogOpen(false);
    queueDeletedPersistedProducts(products);
    (methods as any).setValue('transportationCommoditiesCode', null, { shouldDirty: true, shouldValidate: true, shouldTouch: true });
    methods.setValue('products', [], { shouldDirty: true, shouldValidate: true, shouldTouch: true });
    openCustomerDialog();
  };

  const handleCancelClearProducts = () => {
    setClearProductsDialogOpen(false);
  };

  const getCustomerValue = (customer: Customer, ...keys: string[]) => {
    const candidate = customer as Customer & Record<string, unknown>;
    for (const key of keys) {
      const value = candidate[key];
      if (value != null && String(value).trim() !== '') {
        return String(value).trim();
      }
    }
    return '';
  };

  const extractLookupCode = (value: string) => {
    const normalizedValue = String(value ?? '').trim();
    if (!normalizedValue) {
      return '';
    }

    const separator = ' - ';
    const separatorIndex = normalizedValue.indexOf(separator);
    if (separatorIndex > 0) {
      return normalizedValue.slice(0, separatorIndex).trim();
    }

    return normalizedValue;
  };

  const getRouteFieldValue = (route: Route, keys: string[]) => {
    const candidate = route as Route & Record<string, unknown>;

    for (const key of keys) {
      const value = candidate[key];
      if (value != null && String(value).trim() !== '') {
        return String(value).trim();
      }
    }

    return '';
  };

  const setRouteFieldValue = (
    fieldName:
      | 'routeCode'
      | 'routeDescription'
      | 'transitDays'
      | 'carrierNumber'
      | 'loadLocationId'
      | 'dischargeLocationId'
      | 'plannedLoadDate'
      | 'transportationPlannedDate'
      | 'modeOfTransportation',
    value: string,
    markDirty: boolean,
  ) => {
    const currentValue = String(methods.getValues(fieldName) ?? '');
    if (currentValue === value) {
      return;
    }

    methods.setValue(fieldName, value, {
      shouldDirty: markDirty,
      shouldValidate: true,
      shouldTouch: markDirty,
    });
  };

  const clearRouteFields = (markDirty = true) => {
    setRouteFieldValue('routeCode', '', markDirty);
    setRouteFieldValue('routeDescription', '', markDirty);
    setRouteFieldValue('transitDays', '', markDirty);
    setRouteFieldValue('carrierNumber', '', markDirty);
    setRouteFieldValue('loadLocationId', '', markDirty);
    setRouteFieldValue('dischargeLocationId', '', markDirty);
    setRouteFieldValue('plannedLoadDate', '', markDirty);
    setRouteFieldValue('transportationPlannedDate', '', markDirty);
    setRouteDetailsError(false);
  };

  const setRouteFields = (route: Route, markDirty = true) => {
    const routeCodeValue = getRouteFieldValue(route, ['routeCode', 'routeId', 'RouteCode', 'RouteId']);
    const routeDescriptionValue = getRouteFieldValue(route, ['description', 'routeDescription', 'Description']);
    const transitDaysValue = getRouteFieldValue(route, ['transitDays', 'TransitDays']);
    const carrierNumberValue = getRouteFieldValue(route, ['carrierNumber', 'carrier', 'Carrier']);
    const modeOfTransportationValue = getRouteFieldValue(route, ['modeOfTransportation', 'ModeOfTransportation']);
    const loadLocationValue = getRouteFieldValue(route, ['pointOfOrigin', 'PointOfOrigin', 'originPlant', 'OriginPlant', 'loadLocationId']);
    const dischargeLocationValue = getRouteFieldValue(route, ['pointOfDestination', 'PointOfDestination', 'destinationPlant', 'DestinationPlant', 'dischargeLocationId']);

    setRouteFieldValue('routeCode', routeCodeValue, markDirty);
    setRouteFieldValue('routeDescription', routeDescriptionValue, markDirty);
    setRouteFieldValue('transitDays', transitDaysValue, markDirty);
    setRouteFieldValue('carrierNumber', carrierNumberValue, markDirty);
    setRouteFieldValue('loadLocationId', loadLocationValue, markDirty);
    setRouteFieldValue('dischargeLocationId', dischargeLocationValue, markDirty);
    setRouteFieldValue('modeOfTransportation', modeOfTransportationValue, markDirty);
    methods.clearErrors('routeCode');
    setRouteDetailsError(false);
  };

  const handleSelectCustomer = (customer: Customer) => {
    const customerWithAddress = customer as Customer & { address?: Partial<Record<'street' | 'city' | 'state' | 'postalCode' | 'country', string>> };
    // Raw API returns different field names (soldTo, shipTo) than form fields (soldToNumber, shipToNumber);
    const soldToNumber = getCustomerValue(customer, 'soldTo', 'soldToNumber', 'customerId', 'customerSoldToCustomerNo');
    const soldToName = getCustomerValue(customer, 'soldToName', 'customerName', 'customerSoldToName');
    const shipToNumber = getCustomerValue(customer, 'shipTo', 'shipToNumber', 'shipToPlantCode');
    const shipToName = getCustomerValue(customer, 'shipToName', 'shipToPlantName', 'shipToDescription', 'customerShipToName', 'description');
    const addressLine1 = getCustomerValue(customer, 'addressLine1', 'street') || String(customerWithAddress.address?.street ?? '').trim();
    const addressLine2 = getCustomerValue(customer, 'addressLine2');
    const city = getCustomerValue(customer, 'city') || String(customerWithAddress.address?.city ?? '').trim();
    const state = getCustomerValue(customer, 'state', 'stateProvince') || String(customerWithAddress.address?.state ?? '').trim();
    const country = getCustomerValue(customer, 'country') || String(customerWithAddress.address?.country ?? '').trim();
    const postalCode = getCustomerValue(customer, 'zip', 'zipCode', 'postalCode', 'postalZipCode') || String(customerWithAddress.address?.postalCode ?? '').trim();
    const salesOrg = getCustomerValue(customer, 'sellingSalesOrg', 'sellingSalesOrgNumber', 'salesOrg');
    const distributionChannel = getCustomerValue(customer, 'distributionChannel');
    const division = getCustomerValue(customer, 'division');
    const criticalCustomer = getCustomerValue(customer, 'criticalCustomer').toUpperCase() === 'Y';
    const contactName = getCustomerValue(customer, 'contactName');
    const contactEmail = getCustomerValue(customer, 'email');
    const contactPhoneNumber = getCustomerValue(customer, 'telephone', 'contactPhoneNumber');
    const responsibleEmployee = getCustomerValue(customer, 'responsibleEmployee');
    const responsibleEmployeeName = getCustomerValue(customer, 'responsibleEmployeeName');
    const freightForwarder = getCustomerValue(customer, 'freightForwarderId', 'custFreightForwarderId');
    const drcCode = getCustomerValue(customer, 'drcCodeId', 'custDrcCodeId');
    const drcCodeDescValue = getCustomerValue(customer, 'drcCodeDesc', 'custDrcCodeDesc');
    const drcCodeCountry = getCustomerValue(customer, 'drcCodeCountry', 'custDrcCodeCountry');
    const drcCodeRegion = getCustomerValue(customer, 'drcCodeRegion', 'custDrcCodeRegion');

    methods.setValue('customerId', soldToNumber, { shouldDirty: true, shouldValidate: true, shouldTouch: true });
    methods.setValue('soldToNumber', soldToNumber, { shouldDirty: true, shouldValidate: true, shouldTouch: true });
    methods.setValue('customerName', soldToName, { shouldDirty: true, shouldValidate: true, shouldTouch: true });
    methods.setValue('shipToNumber', shipToNumber, { shouldDirty: true, shouldValidate: true, shouldTouch: true });
    methods.setValue('shipToName', shipToName, { shouldDirty: true, shouldValidate: true, shouldTouch: true });
    methods.setValue('shipToAddressLine1', addressLine1, { shouldDirty: true, shouldValidate: true, shouldTouch: true });
    methods.setValue('shipToAddressLine2', addressLine2, { shouldDirty: true, shouldValidate: true, shouldTouch: true });
    methods.setValue('shipToCity', city, { shouldDirty: true, shouldValidate: true, shouldTouch: true });
    methods.setValue('shipToState', state, { shouldDirty: true, shouldValidate: true, shouldTouch: true });
    methods.setValue('shipToCountry', country, { shouldDirty: true, shouldValidate: true, shouldTouch: true });
    methods.setValue('shipToPostalCode', postalCode, { shouldDirty: true, shouldValidate: true, shouldTouch: true });
    methods.setValue('salesOrg', salesOrg, { shouldDirty: true, shouldValidate: true, shouldTouch: true });
    methods.setValue('distributionChannel', distributionChannel, { shouldDirty: true, shouldValidate: true, shouldTouch: true });
    methods.setValue('division', division, { shouldDirty: true, shouldValidate: true, shouldTouch: true });
    methods.setValue('criticalCustomer', criticalCustomer, { shouldDirty: true, shouldValidate: true, shouldTouch: true });
    methods.setValue('contactName', contactName, { shouldDirty: true, shouldValidate: true, shouldTouch: true });
    methods.setValue('contactEmail', contactEmail, { shouldDirty: true, shouldValidate: true, shouldTouch: true });
    methods.setValue('contactPhoneNumber', contactPhoneNumber, { shouldDirty: true, shouldValidate: true, shouldTouch: true });
    methods.setValue('responsibleEmployee', responsibleEmployee, { shouldDirty: true, shouldValidate: true, shouldTouch: true });
    methods.setValue('responsibleEmployeeName', responsibleEmployeeName, { shouldDirty: true, shouldValidate: true, shouldTouch: true });
    methods.setValue('freightForwarder', freightForwarder, { shouldDirty: true, shouldValidate: true, shouldTouch: true });
    methods.setValue('drcCode', drcCode, { shouldDirty: true, shouldValidate: true, shouldTouch: true });
    methods.setValue('drcCodeDesc', drcCodeDescValue, { shouldDirty: true, shouldValidate: true, shouldTouch: true });
    methods.setValue('drcCodeCountry', drcCodeCountry, { shouldDirty: true, shouldValidate: true, shouldTouch: true });
    methods.setValue('drcCodeRegion', drcCodeRegion, { shouldDirty: true, shouldValidate: true, shouldTouch: true });

    clearNotifyPartnerFields(false);
    clearConsigneeFields(false);
    methods.setValue('freightPayerNumber', '', { shouldDirty: false, shouldValidate: true, shouldTouch: false });

    methods.trigger('shippingCondition');
    methods.trigger('requestedDeliveryDate');
    methods.trigger('requestedPickupDate');

    setCustomerAutoPopulateContext({
      requestId: Date.now(),
      shipToNumber,
      salesOrg,
      distributionChannel,
      division,
      destinationCountry: country,
    });

    setCustomerDialogOpen(false);
    setCustomerSearchInput('');
    setCustomerSearchTerm('');
    setCustomerPage(0);
  };

  const getFreightForwarderValue = (forwarder: FreightForwarder, ...keys: string[]) => {
    const candidate = forwarder as FreightForwarder & Record<string, unknown>;
    for (const key of keys) {
      const value = candidate[key];
      if (value != null && String(value).trim() !== '') {
        return String(value).trim();
      }
    }
    return '';
  };

  const getNotifyPartnerValue = (notifyPartner: NotifyPartner, ...keys: string[]) => {
    const candidate = notifyPartner as NotifyPartner & Record<string, unknown>;
    for (const key of keys) {
      const value = candidate[key];
      if (value != null && String(value).trim() !== '') {
        return String(value).trim();
      }
    }
    return '';
  };

  const openNotifyPartnerDialog = () => {
    if (disableNotifyPartnerModal) {
      return;
    }

    setNotifyPartnerSearchInput('');
    setNotifyPartnerSearchTerm('');
    setNotifyPartnerPage(0);
    setNotifyPartnerRowsPerPage(5);
    setNotifyPartnerDialogOpen(true);
  };

  const handleSelectNotifyPartner = (notifyPartner: NotifyPartner) => {
    setNotifyPartnerFields({
      code: getNotifyPartnerValue(notifyPartner, 'notifyPartnerNumber', 'partnerId'),
      name: getNotifyPartnerValue(notifyPartner, 'name', 'partnerName'),
      addressLine1: getNotifyPartnerValue(notifyPartner, 'address1'),
      addressLine2: getNotifyPartnerValue(notifyPartner, 'address2'),
      city: getNotifyPartnerValue(notifyPartner, 'city'),
      state: getNotifyPartnerValue(notifyPartner, 'state'),
      country: getNotifyPartnerValue(notifyPartner, 'country'),
      postalCode: getNotifyPartnerValue(notifyPartner, 'zipCode'),
      email: getNotifyPartnerValue(notifyPartner, 'eMail'),
    });

    setNotifyPartnerDialogOpen(false);
    setNotifyPartnerSearchInput('');
    setNotifyPartnerSearchTerm('');
    setNotifyPartnerPage(0);
  };

  const handleSelectFreightForwarder = (forwarder: FreightForwarder) => {
    const number = getFreightForwarderValue(forwarder, 'freightFowarderNumber', 'freightForwarderNumber', 'code');
    methods.setValue('freightForwarder', number, { shouldDirty: true, shouldValidate: true, shouldTouch: true });
    setFreightForwarderDialogOpen(false);
  };

  const getConsigneeValue = (consignee: Consignee, ...keys: string[]) => {
    const candidate = consignee as Consignee & Record<string, unknown>;
    for (const key of keys) {
      const value = key.includes('.')
        ? key.split('.').reduce<unknown>((currentValue, segment) => {
          if (currentValue == null || typeof currentValue !== 'object') {
            return undefined;
          }
          return (currentValue as Record<string, unknown>)[segment];
        }, candidate)
        : candidate[key];
      if (value != null && String(value).trim() !== '') {
        return String(value).trim();
      }
    }
    return '';
  };

  const openConsigneeDialog = () => {
    setConsigneeSearchInput('');
    setConsigneeSearchTerm('');
    setConsigneePage(0);
    setConsigneeRowsPerPage(5);
    setConsigneeDialogOpen(true);
  };

  const handleSelectConsignee = (consignee: Consignee) => {
    const selectedConsigneeSnapshot = {
      code: getConsigneeValue(consignee, 'consigneeCode', 'consigneeNumber'),
      name: getConsigneeValue(consignee, 'consigneeName', 'name'),
      addressLine1: getConsigneeValue(consignee, 'addressLine1', 'address1'),
      addressLine2: getConsigneeValue(consignee, 'addressLine2', 'address2', 'AddressLine2', 'Address2', 'line2', 'address.line2'),
      city: getConsigneeValue(consignee, 'city'),
      state: getConsigneeValue(consignee, 'state'),
      country: getConsigneeValue(consignee, 'country'),
      postalCode: getConsigneeValue(consignee, 'postalCode', 'zipCode'),
      email: getConsigneeValue(consignee, 'email', 'eMail'),
    };

    setConsigneeFields(selectedConsigneeSnapshot);

    setConsigneeDialogOpen(false);
    setConsigneeSearchInput('');
    setConsigneeSearchTerm('');
    setConsigneePage(0);
  };

  const handleSelectDrcCode = (drcCode: DrcCode) => {
    methods.setValue('drcCode', drcCode.drcCodeNumber ?? '', { shouldDirty: true, shouldValidate: true, shouldTouch: true });
    methods.setValue('drcCodeDesc', drcCode.drcCodeDesc ?? '', { shouldDirty: true, shouldValidate: true, shouldTouch: true });
    methods.setValue('drcCodeCountry', drcCode.drcCodeCountry ?? '', { shouldDirty: true, shouldValidate: true, shouldTouch: true });
    methods.setValue('drcCodeRegion', drcCode.drcCodeRegion ?? '', { shouldDirty: true, shouldValidate: true, shouldTouch: true });
    setDrcCodeDialogOpen(false);
  };

  const handleSelectSupplyPoint = (supplyPoint: SupplyPoint) => {
    clearRouteFields(true);

    methods.setValue('supplyPointId', getSupplyPointValue(supplyPoint, ['supplyPointId', 'id', 'supplyPointCode', 'SupplyPointPlantNumber']), {
      shouldDirty: true,
      shouldValidate: true,
      shouldTouch: true,
    });
    methods.setValue('supplyPointCode', getSupplyPointValue(supplyPoint, ['supplyPointCode', 'SupplyPointPlantNumber', 'supplyPointId', 'id']), {
      shouldDirty: true,
      shouldValidate: true,
      shouldTouch: true,
    });
    methods.setValue('supplyPointName', getSupplyPointValue(supplyPoint, ['description', 'SupplyPointDescription', 'supplyPointName', 'name']), {
      shouldDirty: true,
      shouldValidate: true,
      shouldTouch: true,
    });
    methods.setValue('supplyPointAddress1', getSupplyPointValue(supplyPoint, ['address', 'Address', 'addressLine1', 'AddressLine1']), {
      shouldDirty: true,
      shouldValidate: true,
      shouldTouch: true,
    });
    methods.setValue('supplyPointAddress2', getSupplyPointValue(supplyPoint, ['addressLine2', 'AddressLine2']), {
      shouldDirty: true,
      shouldValidate: true,
      shouldTouch: true,
    });
    methods.setValue('supplyPointCity', getSupplyPointValue(supplyPoint, ['city', 'City']), {
      shouldDirty: true,
      shouldValidate: true,
      shouldTouch: true,
    });
    methods.setValue('supplyPointState', getSupplyPointValue(supplyPoint, ['state', 'StateProvince', 'stateProvince', 'region']), {
      shouldDirty: true,
      shouldValidate: true,
      shouldTouch: true,
    });
    methods.setValue('supplyPointZipCode', getSupplyPointValue(supplyPoint, ['zip', 'Zip', 'zipCode', 'PostalZipCode', 'postalZipCode']), {
      shouldDirty: true,
      shouldValidate: true,
      shouldTouch: true,
    });
    methods.setValue('supplyPointCountry', getSupplyPointValue(supplyPoint, ['country', 'Country']), {
      shouldDirty: true,
      shouldValidate: true,
      shouldTouch: true,
    });
    setSelectedSupplyPointTimeZone(getSupplyPointValue(supplyPoint, ['supplyPointTimeZone', 'SupplyPointTimeZone']));

    setSupplyPointDialogOpen(false);
  };

  const handleDeleteSupplyPoint = () => {
    methods.setValue('supplyPointId', '', { shouldDirty: true, shouldValidate: true, shouldTouch: true });
    methods.setValue('supplyPointCode', '', { shouldDirty: true, shouldValidate: true, shouldTouch: true });
    methods.setValue('supplyPointName', '', { shouldDirty: true, shouldValidate: true, shouldTouch: true });
    methods.setValue('supplyPointAddress1', '', { shouldDirty: true, shouldValidate: true, shouldTouch: true });
    methods.setValue('supplyPointAddress2', '', { shouldDirty: true, shouldValidate: true, shouldTouch: true });
    methods.setValue('supplyPointCity', '', { shouldDirty: true, shouldValidate: true, shouldTouch: true });
    methods.setValue('supplyPointState', '', { shouldDirty: true, shouldValidate: true, shouldTouch: true });
    methods.setValue('supplyPointZipCode', '', { shouldDirty: true, shouldValidate: true, shouldTouch: true });
    methods.setValue('supplyPointCountry', '', { shouldDirty: true, shouldValidate: true, shouldTouch: true });
    setSelectedSupplyPointTimeZone('');
    clearRouteFields();
  };

  const handleSelectRoute = (route: Route) => {
    setRouteFields(route);
    setRouteDialogOpen(false);
  };

  const handleSelectContainerType = (containerType: ContainerType) => {
    methods.setValue('containerType', containerType.code ?? '', {
      shouldDirty: true,
      shouldValidate: true,
      shouldTouch: true,
    });
    setContainerTypeDialogOpen(false);
  };

  const openShippingConditionDialog = () => {
    if (typeof document !== 'undefined') {
      const shippingConditionInput = document.getElementById('qa-shippingCondition-ipt') as HTMLInputElement | null;
      shippingConditionInput?.focus();
      shippingConditionInput?.scrollIntoView({ block: 'center', behavior: 'smooth' });
    }

    setShippingConditionDialogOpen(true);
  };

  const handleSelectShippingCondition = (condition: ShippingCondition) => {
    const code = getShippingConditionLookupCode(condition);
    const description = getShippingConditionLookupDescription(condition);
    const conditionRecord = condition as ShippingCondition & Record<string, unknown>;
    const modeOfTransportation = String(conditionRecord.modeOfTransportation ?? '').trim();

    methods.setValue('shippingCondition', composeLookupDisplay(code, description), {
      shouldDirty: true,
      shouldValidate: true,
      shouldTouch: true,
    });
    methods.setValue('modeOfTransportation', modeOfTransportation, {
      shouldDirty: true,
      shouldValidate: true,
      shouldTouch: true,
    });

    const currentIncoterm = String(methods.getValues('incoterm') ?? '').trim();
    if (!currentIncoterm) {
      const incotermOptions = incotermsQuery.data ?? [];
      const dependentIncotermCode = String(
        conditionRecord.incoterm
          ?? conditionRecord.incoterms1
          ?? conditionRecord.inco1
          ?? conditionRecord.Incoterm
          ?? conditionRecord.Incoterms1
          ?? conditionRecord.Inco1
          ?? '',
      ).trim();

      const matchingDependentOption = dependentIncotermCode
        ? incotermOptions.find((term) => normalizeLookupCode(getIncotermLookupCode(term)) === normalizeLookupCode(dependentIncotermCode))
        : undefined;

      const fcaOption = incotermOptions.find((term) => {
        const termCode = normalizeLookupCode(getIncotermLookupCode(term)).toUpperCase();
        const termDescription = String(getIncotermLookupDescription(term) ?? '').trim().toUpperCase();
        return termCode === 'FCA' || termDescription === 'FREE CARRIER';
      });

      const selectedIncotermCode = matchingDependentOption
        ? getIncotermLookupCode(matchingDependentOption)
        : (fcaOption ? getIncotermLookupCode(fcaOption) : 'FCA');

      const selectedIncotermDescription = matchingDependentOption
        ? getIncotermLookupDescription(matchingDependentOption)
        : (fcaOption ? getIncotermLookupDescription(fcaOption) : 'Free Carrier');

      methods.setValue('incoterm', composeLookupDisplay(selectedIncotermCode, selectedIncotermDescription), {
        shouldDirty: true,
        shouldValidate: true,
        shouldTouch: true,
      });
    }

    setShippingConditionDialogOpen(false);
    setShippingConditionSearchInput('');
  };

  const normalizeSearchValue = (value: string) => value.trim().toLowerCase();

  const shippingConditionRows = useMemo(() => {
    const rows = (shippingConditionsQuery.data ?? []) as ShippingCondition[];
    const normalizedSearch = normalizeSearchValue(shippingConditionSearchInput);

    if (!normalizedSearch) {
      return rows;
    }

    return rows.filter((condition) => {
      const code = normalizeSearchValue(getShippingConditionLookupCode(condition));
      const description = normalizeSearchValue(getShippingConditionLookupDescription(condition));
      return code.includes(normalizedSearch) || description.includes(normalizedSearch);
    });
  }, [shippingConditionSearchInput, shippingConditionsQuery.data]);

  const shippingConditionColumns: SelectionDialogColumn<ShippingCondition>[] = useMemo(() => [
    { key: 'shippingConditionId', header: 'shippingConditionId', render: (condition) => getShippingConditionLookupCode(condition) },
    { key: 'shippingConditionDescription', header: 'shippingConditionDescription', render: (condition) => getShippingConditionLookupDescription(condition) },
  ], []);

  const customerColumns: SelectionDialogColumn<Customer>[] = useMemo(() => [
    { key: 'soldTo', header: 'Sold-To', render: (customer) => getCustomerValue(customer, 'soldTo', 'soldToNumber', 'customerId', 'customerSoldToCustomerNo') },
    { key: 'soldToName', header: 'Sold-To-Name', render: (customer) => getCustomerValue(customer, 'soldToName', 'customerName', 'customerSoldToName') },
    { key: 'shipTo', header: 'Ship-To', render: (customer) => getCustomerValue(customer, 'shipTo', 'shipToNumber', 'shipToPlantCode') },
    { key: 'shipToName', header: 'Ship-To Description', render: (customer) => getCustomerValue(customer, 'shipToName', 'shipToPlantName', 'shipToDescription', 'customerShipToName', 'description'), sx: { minWidth: 150 } },
    {
      key: 'address',
      header: 'Address',
      render: (customer) => {
        const customerRow = customer as Customer & { address?: Partial<Record<'street', string>> };
        return getCustomerValue(customer, 'addressLine1', 'street') || String(customerRow.address?.street ?? '').trim();
      },
      sx: { minWidth: 150 },
    },
    {
      key: 'city',
      header: 'City',
      render: (customer) => {
        const customerRow = customer as Customer & { address?: Partial<Record<'city', string>> };
        return getCustomerValue(customer, 'city') || String(customerRow.address?.city ?? '').trim();
      },
    },
    {
      key: 'state',
      header: 'State/Prov',
      render: (customer) => {
        const customerRow = customer as Customer & { address?: Partial<Record<'state', string>> };
        return getCustomerValue(customer, 'state', 'stateProvince') || String(customerRow.address?.state ?? '').trim();
      },
      sx: { maxWidth: 90 },
    },
    {
      key: 'country',
      header: 'CO',
      render: (customer) => {
        const customerRow = customer as Customer & { address?: Partial<Record<'country', string>> };
        return getCustomerValue(customer, 'country') || String(customerRow.address?.country ?? '').trim();
      },
      sx: { maxWidth: 90 },
    },
    {
      key: 'zip',
      header: 'Zip',
      render: (customer) => {
        const customerRow = customer as Customer & { address?: Partial<Record<'postalCode', string>> };
        return getCustomerValue(customer, 'zip', 'zipCode', 'postalCode', 'postalZipCode') || String(customerRow.address?.postalCode ?? '').trim();
      },
      sx: { maxWidth: 90 },
    },
    { key: 'salesOrg', header: 'Sales Org', render: (customer) => getCustomerValue(customer, 'sellingSalesOrg', 'sellingSalesOrgNumber', 'salesOrg'), sx: { maxWidth: 100 } },
    { key: 'channel', header: 'Chan', render: (customer) => getCustomerValue(customer, 'distributionChannel'), sx: { maxWidth: 90 } },
    { key: 'division', header: 'Div', render: (customer) => getCustomerValue(customer, 'division'), sx: { maxWidth: 90 } },
  ], []);

  const freightForwarderColumns: SelectionDialogColumn<FreightForwarder>[] = useMemo(() => [
    { key: 'number', header: 'Number', render: (forwarder) => getFreightForwarderValue(forwarder, 'freightFowarderNumber', 'freightForwarderNumber', 'code') },
    { key: 'name', header: 'Name', render: (forwarder) => getFreightForwarderValue(forwarder, 'freightFowarderName', 'freightForwarderName', 'description') },
    { key: 'city', header: 'City', render: (forwarder) => String((forwarder as FreightForwarder & Record<string, unknown>).city ?? '').trim() },
    { key: 'country', header: 'Country', render: (forwarder) => String((forwarder as FreightForwarder & Record<string, unknown>).country ?? '').trim() },
  ], []);

  const notifyPartnerColumns: SelectionDialogColumn<NotifyPartner>[] = useMemo(() => [
    { key: 'name', header: 'Name', render: (notifyPartner) => getNotifyPartnerValue(notifyPartner, 'name', 'partnerName') },
    { key: 'address1', header: 'Address', render: (notifyPartner) => getNotifyPartnerValue(notifyPartner, 'address1'), sx: { minWidth: 150 } },
    { key: 'city', header: 'City', render: (notifyPartner) => getNotifyPartnerValue(notifyPartner, 'city') },
    { key: 'state', header: 'State/Prov', render: (notifyPartner) => getNotifyPartnerValue(notifyPartner, 'state'), sx: { maxWidth: 100 } },
    { key: 'country', header: 'Country', render: (notifyPartner) => getNotifyPartnerValue(notifyPartner, 'country'), sx: { maxWidth: 100 } },
    { key: 'zipCode', header: 'Zip', render: (notifyPartner) => getNotifyPartnerValue(notifyPartner, 'zipCode') },
  ], []);

  const consigneeColumns: SelectionDialogColumn<Consignee>[] = useMemo(() => [
    { key: 'consigneeCode', header: 'Consignee Code', render: (consignee) => getConsigneeValue(consignee, 'consigneeCode', 'consigneeNumber') },
    { key: 'consigneeName', header: 'Consignee Name', render: (consignee) => getConsigneeValue(consignee, 'consigneeName', 'name') },
    { key: 'address', header: 'Address', render: (consignee) => getConsigneeValue(consignee, 'addressLine1', 'address1'), sx: { minWidth: 150 } },
    { key: 'city', header: 'City', render: (consignee) => getConsigneeValue(consignee, 'city') },
    { key: 'state', header: 'State/Prov', render: (consignee) => getConsigneeValue(consignee, 'state'), sx: { maxWidth: 100 } },
    { key: 'country', header: 'Country', render: (consignee) => getConsigneeValue(consignee, 'country'), sx: { maxWidth: 100 } },
    { key: 'postalCode', header: 'Postal Code', render: (consignee) => getConsigneeValue(consignee, 'postalCode', 'zipCode') },
  ], []);

  const drcCodeColumns: SelectionDialogColumn<DrcCode>[] = useMemo(() => [
    { key: 'code', header: 'Code', render: (drcCode) => drcCode.drcCodeNumber },
    { key: 'desc', header: 'Desc', render: (drcCode) => drcCode.drcCodeDesc },
    { key: 'country', header: 'Country', render: (drcCode) => drcCode.drcCodeCountry },
    { key: 'region', header: 'Region', render: (drcCode) => drcCode.drcCodeRegion },
  ], []);

  const supplyPointColumns: SelectionDialogColumn<SupplyPoint>[] = useMemo(() => [
    {
      key: 'code',
      header: 'Code',
      render: (supplyPoint) => getSupplyPointValue(supplyPoint, ['supplyPointCode', 'SupplyPointPlantNumber', 'supplyPointId', 'id']),
    },
    {
      key: 'description',
      header: 'Description',
      render: (supplyPoint) => getSupplyPointValue(supplyPoint, ['description', 'SupplyPointDescription', 'supplyPointName', 'name']),
    },
    {
      key: 'address',
      header: 'Address',
      render: (supplyPoint) => getSupplyPointValue(supplyPoint, ['address', 'Address', 'addressLine1', 'AddressLine1']),
      sx: { minWidth: 150 },
    },
    { key: 'city', header: 'City', render: (supplyPoint) => getSupplyPointValue(supplyPoint, ['city', 'City']) },
    { key: 'state', header: 'State/Prov', render: (supplyPoint) => getSupplyPointValue(supplyPoint, ['state', 'StateProvince', 'stateProvince', 'region']), sx: { maxWidth: 90 } },
    { key: 'country', header: 'CO', render: (supplyPoint) => getSupplyPointValue(supplyPoint, ['country', 'Country']), sx: { maxWidth: 90 } },
    {
      key: 'zip',
      header: 'Zip',
      render: (supplyPoint) => getSupplyPointValue(supplyPoint, ['zip', 'Zip', 'zipCode', 'PostalZipCode', 'postalZipCode']),
      sx: { maxWidth: 90 },
    },
  ], []);

  const routeColumns: SelectionDialogColumn<Route>[] = useMemo(() => [
    { key: 'routeCode', header: 'Route Code', render: (route) => route.routeCode ?? route.routeId },
    { key: 'description', header: 'Description', render: (route) => route.description ?? route.routeDescription },
    { key: 'transitDays', header: 'Transit Days', render: (route) => String(route.transitDays ?? '') },
    { key: 'carrier', header: 'Carrier', render: (route) => route.carrier ?? '' },
  ], []);

  const onSubmit = async (values: OrderFormValues) => {
    const payload: Order = {
      ...order,
      orderNumber: values.orderNumber || order.orderNumber,
      creatorId: values.creatorId || undefined,
      orderType: values.orderType,
      orderTypeCode: values.orderType,
      orderTypeDescription: getOrderTypeDescription(values.orderType)
        ?? orderTypesQuery.data?.find((ot) => ot.orderTypeCode.toUpperCase() === values.orderType.toUpperCase())?.orderTypeDescription
        ?? values.orderType,
      status: values.status,
      responsibleEmployee: values.responsibleEmployee || undefined,
      responsibleEmployeeName: values.responsibleEmployeeName || undefined,
      customerConfirmation: values.customerConfirmation,
      preCyberStatus: values.preCyberStatus || undefined,
      preCyberOrderNumber: values.preCyberOrderNumber || undefined,
      customerId: values.customerId,
      soldToNumber: values.soldToNumber || values.customerId,
      customerName: values.customerName,
      purchaseOrderNumber: values.purchaseOrderNumber || undefined,
      contactName: values.contactName || undefined,
      contactEmail: values.contactEmail || undefined,
      contactPhoneNumber: values.contactPhoneNumber || undefined,
      salesOrg: values.salesOrg,
      sellingSalesOrg: values.salesOrg,
      distributionChannel: values.distributionChannel || undefined,
      division: values.division || undefined,
      requestedDeliveryDate: values.isPickup === 'pickup' ? '' : values.requestedDeliveryDate,
      requestedPickupDate: values.isPickup === 'pickup' ? values.requestedPickupDate : undefined,
      requestedDeliveryTime: values.requestedDeliveryTime || undefined,
      deliveryDate: values.deliveryDate || undefined,
      shippingCondition: values.shippingCondition,
      modeOfTransportation: values.modeOfTransportation,
      isPickup: values.isPickup === 'pickup',
      supplyPointCode: values.supplyPointCode || undefined,
      supplyPointName: values.supplyPointName || undefined,
      supplyPointAddress1: values.supplyPointAddress1 || undefined,
      supplyPointAddress2: values.supplyPointAddress2 || undefined,
      supplyPointCity: values.supplyPointCity || undefined,
      supplyPointState: values.supplyPointState || undefined,
      supplyPointZipCode: values.supplyPointZipCode || undefined,
      supplyPointCountry: values.supplyPointCountry || undefined,
      routeCode: values.routeCode || undefined,
      routeDescription: values.routeDescription || undefined,
      transitDays: values.transitDays ? Number(values.transitDays) : undefined,
      plannedLoadDate: values.plannedLoadDate || undefined,
      transportationPlannedDate: values.transportationPlannedDate || undefined,
      carrierNumber: values.carrierNumber || undefined,
      incoterm: values.incoterm || undefined,
      incotermLocation: values.incotermLocation || undefined,
      freightForwarder: values.freightForwarder || undefined,
      freightForwarderId: values.freightForwarder || undefined,
      drcCode: values.drcCode || undefined,
      drcCodeId: values.drcCode || undefined,
      drcCodeDesc: values.drcCodeDesc || undefined,
      drcCodeCountry: values.drcCodeCountry || undefined,
      drcCodeRegion: values.drcCodeRegion || undefined,
      shipToNumber: values.shipToNumber || undefined,
      shipToName: values.shipToName || undefined,
      shipToPlantCode: values.shipToNumber || undefined,
      shipToPlantName: values.shipToName || undefined,
      shipToAddressLine1: values.shipToAddressLine1 || undefined,
      shipToAddressLine2: values.shipToAddressLine2 || undefined,
      shipToCity: values.shipToCity || undefined,
      shipToState: values.shipToState || undefined,
      shipToPostalCode: values.shipToPostalCode || undefined,
      shipToCountry: values.shipToCountry || undefined,
      shipToAddress: {
        name: values.shipToName || '',
        line1: values.shipToAddressLine1 || undefined,
        line2: values.shipToAddressLine2 || undefined,
        street: values.shipToAddressLine1 || '',
        city: values.shipToCity || '',
        state: values.shipToState || '',
        postalCode: values.shipToPostalCode || '',
        country: values.shipToCountry || '',
      },
      notifyPartnerCode: values.notifyPartnerCode || undefined,
      notifyPartnerName: values.notifyPartnerName || undefined,
      notifyPartner: {
        partnerId: values.notifyPartnerCode || '',
        partnerName: values.notifyPartnerName || '',
        notifyPartnerNumber: values.notifyPartnerCode || undefined,
        name: values.notifyPartnerName || undefined,
        address1: values.notifyPartnerAddressLine1 || undefined,
        address2: values.notifyPartnerAddressLine2 || undefined,
        city: values.notifyPartnerCity || undefined,
        state: values.notifyPartnerState || undefined,
        country: values.notifyPartnerCountry || undefined,
        zipCode: values.notifyPartnerPostalCode || undefined,
        eMail: values.notifyPartnerEmail || undefined,
        address: {
          name: values.notifyPartnerName || '',
          line1: values.notifyPartnerAddressLine1 || undefined,
          line2: values.notifyPartnerAddressLine2 || undefined,
          street: values.notifyPartnerAddressLine1 || '',
          city: values.notifyPartnerCity || '',
          state: values.notifyPartnerState || '',
          postalCode: values.notifyPartnerPostalCode || '',
          country: values.notifyPartnerCountry || '',
        },
      },
      criticalCustomer: values.criticalCustomer,
      numberOfTrucks: values.numberOfTrucks !== '' ? Number(values.numberOfTrucks) : undefined,
      containerType: values.containerType || undefined,
      numberOfContainers: values.numberOfContainers ? Number(values.numberOfContainers) : undefined,
      uniqueRCIDNumber: values.uniqueRCIDNumber || undefined,
      shipmentId: values.shipmentId || undefined,
      freightPayerNumber: values.freightPayerNumber ?? '',
      vesselName: values.vesselName || undefined,
      expectedDischargeDate: values.expectedDischargeDate || undefined,
      loadLocationId: values.loadLocationId || undefined,
      overrideLoadDate: values.overrideLoadDate || undefined,
      surveyInspector: values.surveyInspector || undefined,
      vesselScreeningNumber: values.vesselScreeningNumber || undefined,
      dischargeLocationId: values.dischargeLocationId || undefined,
      consigneePartner: values.consigneeCode ?? '',
      consigneeCode: values.consigneeCode ?? '',
      consigneeName: values.consigneeName || undefined,
      consigneeAddressLine1: values.consigneeAddressLine1 || undefined,
      consigneeAddressLine2: values.consigneeAddressLine2 || undefined,
      consigneeCity: values.consigneeCity || undefined,
      consigneeState: values.consigneeState || undefined,
      consigneeCountry: values.consigneeCountry || undefined,
      consigneePostalCode: values.consigneePostalCode || undefined,
      consigneeEmail: values.consigneeEmail || undefined,
      specialInstructions: values.specialInstructions || undefined,
      haulierText: values.haulierText || undefined,
      manualInvoiceNumber: values.manualInvoiceNumber || undefined,
      products: values.products,
    };

    try {
      setIsSaving(true);
      await onSave(payload, { deletedProducts });
      methods.reset(values);
      setDeletedProducts([]);
      setSkipDiscardPrompt(true);
      setSaveDisabledAfterSave(true);
    } catch (error) {
      setSaveDisabledAfterSave(false);
      setIsSaving(false);
      console.error('❌ Error saving order:', error);
      return;
    }
    
    setIsSaving(false);
  };

  useEffect(() => {
    setSelectedSupplyPointTimeZone(order.supplyPointTimeZone ?? '');
    methods.reset({
      orderNumber: order.orderNumber ?? order.orderId,
      creatorId: order.creatorId ?? '',
      orderPlacedDate: order.orderPlacedDate ?? '',
      orderType: resolveInitialOrderType(order),
      orderTypeDescription: order.orderTypeDescription ?? order.orderType ?? '',
      status: order.status,
      responsibleEmployee: order.responsibleEmployee ?? '',
      responsibleEmployeeName: order.responsibleEmployeeName ?? '',
      customerConfirmation: order.customerConfirmation ?? false,
      preCyberStatus: order.preCyberStatus ?? order.preCyberOrderStatus ?? '',
      preCyberOrderNumber: order.preCyberOrderNumber ?? '',
      customerId: order.customerId ?? '',
      soldToNumber: order.soldToNumber ?? order.customerId ?? '',
      customerName: order.customerName ?? '',
      purchaseOrderNumber: order.purchaseOrderNumber ?? '',
      contactName: order.contactName ?? '',
      contactEmail: order.contactEmail ?? '',
      contactPhoneNumber: order.contactPhoneNumber ?? '',
      shipToAddress,
      shipToAddressLine1: order.shipToAddressLine1 ?? order.shipToAddress?.line1 ?? order.shipToAddress?.street ?? '',
      shipToAddressLine2: order.shipToAddressLine2 ?? order.shipToAddress?.line2 ?? '',
      shipToCity: order.shipToCity ?? order.shipToAddress?.city ?? '',
      shipToState: order.shipToState ?? order.shipToAddress?.state ?? '',
      shipToCountry: order.shipToCountry ?? order.shipToAddress?.country ?? '',
      shipToPostalCode: order.shipToPostalCode ?? order.shipToAddress?.postalCode ?? '',
      shipToEmail: '',
      salesOrg: order.salesOrg ?? order.sellingSalesOrg ?? '',
      distributionChannel: order.distributionChannel ?? '',
      division: order.division ?? '',
      drcCodeDesc: order.drcCodeDesc ?? '',
      drcCodeCountry: order.drcCodeCountry ?? '',
      drcCodeRegion: order.drcCodeRegion ?? '',
      shipToNumber: order.shipToNumber ?? order.shipToPlantCode ?? '',
      shipToName: order.shipToName ?? order.shipToPlantName ?? '',
      notifyPartnerCode: order.notifyPartnerCode ?? '',
      notifyPartnerName: order.notifyPartnerName ?? '',
      notifyPartnerAddressLine1: (order.notifyPartner?.address as Partial<Record<'addressLine1' | 'street', string>> | undefined)?.addressLine1
        ?? order.notifyPartner?.address?.street
        ?? '',
      notifyPartnerAddressLine2: (order.notifyPartner?.address as Partial<Record<'addressLine2' | 'line2', string>> | undefined)?.addressLine2
        ?? order.notifyPartner?.address?.line2
        ?? order.notifyPartner?.address2
        ?? '',
      notifyPartnerCity: order.notifyPartner?.address?.city ?? '',
      notifyPartnerState: order.notifyPartner?.address?.state ?? '',
      notifyPartnerCountry: order.notifyPartner?.address?.country ?? '',
      notifyPartnerPostalCode: (order.notifyPartner?.address as Partial<Record<'zipCode' | 'postalCode', string>> | undefined)?.zipCode
        ?? order.notifyPartner?.address?.postalCode
        ?? '',
      notifyPartnerEmail: (order.notifyPartner?.address as Partial<Record<'eMail', string>> | undefined)?.eMail
        ?? order.notifyPartner?.eMail
        ?? '',
      criticalCustomer: order.criticalCustomer ?? false,
      isPickup: order.isPickup === true ? 'pickup' : 'delivery',
      requestedDeliveryDate: order.isPickup ? '' : normalizeDateForDateInput(order.requestedDeliveryDate),
      requestedPickupDate: order.isPickup ? normalizeDateForDateInput(order.requestedPickupDate ?? order.requestedDeliveryDate) : '',
      requestedDeliveryTime: order.requestedDeliveryTime ?? '',
      deliveryDate: normalizeDateForDateInput(order.deliveryDate),
      shippingCondition: composeLookupDisplay(order.shippingCondition, order.shippingConditionDescription),
      modeOfTransportation: order.modeOfTransportation ?? '',
      supplyPointCode: order.supplyPointCode ?? '',
      supplyPointId: (order as Order & { supplyPointId?: string }).supplyPointId ?? order.supplyPointCode ?? '',
      supplyPointName: order.supplyPointName ?? '',
      supplyPointAddress1: order.supplyPointAddress1 ?? '',
      supplyPointAddress2: order.supplyPointAddress2 ?? '',
      supplyPointCity: order.supplyPointCity ?? '',
      supplyPointState: order.supplyPointState ?? '',
      supplyPointZipCode: order.supplyPointZipCode ?? '',
      supplyPointCountry: order.supplyPointCountry ?? '',
      routeCode: order.routeCode ?? '',
      routeDescription: order.routeDescription ?? '',
      transitDays: order.transitDays != null ? String(order.transitDays) : '',
      plannedLoadDate: normalizeDateForDateInput(order.plannedLoadDate),
      transportationPlannedDate: normalizeDateForDateInput(order.transportationPlannedDate),
      carrierNumber: order.carrierNumber ?? '',
      incoterm: composeLookupDisplay(order.incoterm, order.incotermDescription),
      incotermLocation: order.incotermLocation ?? '',
      freightForwarder: order.freightForwarderId ?? order.freightForwarder ?? '',
      drcCode: order.drcCodeId ?? order.drcCode ?? '',
      numberOfTrucks: order.numberOfTrucks != null ? String(order.numberOfTrucks) : '1',
      containerType: order.containerType ?? '',
      numberOfContainers: order.numberOfContainers != null ? String(order.numberOfContainers) : '1',
      uniqueRCIDNumber: order.uniqueRCIDNumber ?? '',
      shipmentId: order.shipmentId ?? '',
      freightPayerNumber: order.freightPayerNumber ?? '',
      vesselName: order.vesselName ?? '',
      expectedDischargeDate: normalizeDateForDateInput(order.expectedDischargeDate),
      loadLocationId: order.loadLocationId ?? '',
      overrideLoadDate: normalizeDateForDateInput(order.overrideLoadDate),
      surveyInspector: order.surveyInspector ?? '',
      vesselScreeningNumber: order.vesselScreeningNumber ?? '',
      dischargeLocationId: order.dischargeLocationId ?? '',
      consigneeCode: order.consigneeCode ?? order.consigneePartner ?? '',
      consigneeName: order.consigneeName ?? '',
      consigneeAddressLine1: order.consigneeAddressLine1 ?? '',
      consigneeAddressLine2: order.consigneeAddressLine2 ?? '',
      consigneeCity: order.consigneeCity ?? '',
      consigneeState: order.consigneeState ?? '',
      consigneeCountry: order.consigneeCountry ?? '',
      consigneePostalCode: order.consigneePostalCode ?? '',
      consigneeEmail: order.consigneeEmail ?? '',
      specialInstructions: order.specialInstructions ?? '',
      haulierText: order.haulierText ?? '',
      manualInvoiceNumber: order.manualInvoiceNumber ?? '',
      products: order.products ?? [],
    });

    notifyPartnerSnapshotRef.current = {
      code: String(order.notifyPartnerCode ?? '').trim(),
      name: String(order.notifyPartnerName ?? '').trim(),
      addressLine1: String((order.notifyPartner?.address as Partial<Record<'addressLine1' | 'street', string>> | undefined)?.addressLine1 ?? order.notifyPartner?.address?.street ?? '').trim(),
      addressLine2: String(
        (order.notifyPartner?.address as Partial<Record<'addressLine2' | 'line2', string>> | undefined)?.addressLine2
        ?? order.notifyPartner?.address?.line2
        ?? order.notifyPartner?.address2
        ?? '',
      ).trim(),
      city: String(order.notifyPartner?.address?.city ?? '').trim(),
      state: String(order.notifyPartner?.address?.state ?? '').trim(),
      country: String(order.notifyPartner?.address?.country ?? '').trim(),
      postalCode: String((order.notifyPartner?.address as Partial<Record<'zipCode' | 'postalCode', string>> | undefined)?.zipCode ?? order.notifyPartner?.address?.postalCode ?? '').trim(),
      email: String(
        (order.notifyPartner?.address as Partial<Record<'eMail', string>> | undefined)?.eMail
        ?? order.notifyPartner?.eMail
        ?? '',
      ).trim(),
    };
    consigneeSnapshotRef.current = {
      code: String(order.consigneeCode ?? order.consigneePartner ?? '').trim(),
      name: String(order.consigneeName ?? '').trim(),
      addressLine1: String(order.consigneeAddressLine1 ?? '').trim(),
      addressLine2: String(order.consigneeAddressLine2 ?? '').trim(),
      city: String(order.consigneeCity ?? '').trim(),
      state: String(order.consigneeState ?? '').trim(),
      country: String(order.consigneeCountry ?? '').trim(),
      postalCode: String(order.consigneePostalCode ?? '').trim(),
      email: String(order.consigneeEmail ?? '').trim(),
    };
    previousIncotermCodeRef.current = normalizeLookupCode(composeLookupDisplay(order.incoterm, order.incotermDescription));

    void methods.trigger();
  }, [methods, order, shipToAddress]);

  const isPickup = useWatch({
    control: methods.control,
    name: 'isPickup',
  });

  const orderStatus = useWatch({
    control: methods.control,
    name: 'status',
  });

  const transitDays = useWatch({
    control: methods.control,
    name: 'transitDays',
  });

  const routeCode = useWatch({
    control: methods.control,
    name: 'routeCode',
  });

  const requestedDeliveryDate = useWatch({
    control: methods.control,
    name: 'requestedDeliveryDate',
  });

  const requestedPickupDate = useWatch({
    control: methods.control,
    name: 'requestedPickupDate',
  });

  const incotermValue = useWatch({
    control: methods.control,
    name: 'incoterm',
  });

  const notifyPartnerCodeValue = useWatch({
    control: methods.control,
    name: 'notifyPartnerCode',
  });

  const notifyPartnerNameValue = useWatch({
    control: methods.control,
    name: 'notifyPartnerName',
  });

  const notifyPartnerAddressLine1Value = useWatch({
    control: methods.control,
    name: 'notifyPartnerAddressLine1',
  });

  const notifyPartnerAddressLine2Value = useWatch({
    control: methods.control,
    name: 'notifyPartnerAddressLine2',
  });

  const notifyPartnerCityValue = useWatch({
    control: methods.control,
    name: 'notifyPartnerCity',
  });

  const notifyPartnerStateValue = useWatch({
    control: methods.control,
    name: 'notifyPartnerState',
  });

  const notifyPartnerCountryValue = useWatch({
    control: methods.control,
    name: 'notifyPartnerCountry',
  });

  const notifyPartnerPostalCodeValue = useWatch({
    control: methods.control,
    name: 'notifyPartnerPostalCode',
  });

  const notifyPartnerEmailValue = useWatch({
    control: methods.control,
    name: 'notifyPartnerEmail',
  });

  const consigneeCodeValue = useWatch({
    control: methods.control,
    name: 'consigneeCode',
  });

  const consigneeNameValue = useWatch({
    control: methods.control,
    name: 'consigneeName',
  });

  const consigneeAddressLine1Value = useWatch({
    control: methods.control,
    name: 'consigneeAddressLine1',
  });

  const consigneeAddressLine2Value = useWatch({
    control: methods.control,
    name: 'consigneeAddressLine2',
  });

  const consigneeCityValue = useWatch({
    control: methods.control,
    name: 'consigneeCity',
  });

  const consigneeStateValue = useWatch({
    control: methods.control,
    name: 'consigneeState',
  });

  const consigneeCountryValue = useWatch({
    control: methods.control,
    name: 'consigneeCountry',
  });

  const consigneePostalCodeValue = useWatch({
    control: methods.control,
    name: 'consigneePostalCode',
  });

  const consigneeEmailValue = useWatch({
    control: methods.control,
    name: 'consigneeEmail',
  });

  const soldToNumberValue = useWatch({
    control: methods.control,
    name: 'soldToNumber',
  });

  useEffect(() => {
    const previousIsPickup = previousIsPickupRef.current;
    previousIsPickupRef.current = isPickup;

    const didToggle = previousIsPickup != null && previousIsPickup !== isPickup;
    const inactiveRequestedDateField = isPickup === 'pickup' ? 'requestedDeliveryDate' : 'requestedPickupDate';

    methods.setValue(inactiveRequestedDateField, '', {
      shouldDirty: didToggle,
      shouldTouch: didToggle,
      shouldValidate: true,
    });

    if (didToggle) {
      methods.setValue('requestedDeliveryTime', '', {
        shouldDirty: true,
        shouldTouch: true,
        shouldValidate: true,
      });
    }
  }, [isPickup, methods]);

  useEffect(() => {
    const requestedDate = isPickup === 'pickup' ? requestedPickupDate : requestedDeliveryDate;
    if (!requestedDate) {
      methods.setValue('plannedLoadDate', '', { shouldDirty: false, shouldTouch: false, shouldValidate: true });
      methods.setValue('transportationPlannedDate', '', { shouldDirty: false, shouldTouch: false, shouldValidate: true });
      return;
    }

    const baseDate = parseDateInputAsLocalDate(requestedDate);
    if (Number.isNaN(baseDate.getTime())) {
      methods.setValue('plannedLoadDate', '', { shouldDirty: false, shouldTouch: false, shouldValidate: true });
      methods.setValue('transportationPlannedDate', '', { shouldDirty: false, shouldTouch: false, shouldValidate: true });
      return;
    }

    const normalizedRouteCode = String(routeCode ?? '').trim();
    if (!normalizedRouteCode) {
      methods.setValue('transportationPlannedDate', formatLocalDateForInput(baseDate), {
        shouldDirty: false,
        shouldTouch: false,
        shouldValidate: true,
      });
      methods.setValue('plannedLoadDate', formatLocalDateForInput(baseDate), {
        shouldDirty: false,
        shouldTouch: false,
        shouldValidate: true,
      });
      return;
    }

    const requestId = planningDatesRequestIdRef.current + 1;
    planningDatesRequestIdRef.current = requestId;

    void calculatePlanningDates(normalizedRouteCode, baseDate)
      .then((planningDates) => {
        if (planningDatesRequestIdRef.current !== requestId) {
          return;
        }

        methods.setValue('plannedLoadDate', normalizeDateForDateInput(planningDates.plannedLoadDate ?? ''), {
          shouldDirty: false,
          shouldTouch: false,
          shouldValidate: true,
        });
        methods.setValue(
          'transportationPlannedDate',
          normalizeDateForDateInput(planningDates.plannedTransportationDate ?? ''),
          {
            shouldDirty: false,
            shouldTouch: false,
            shouldValidate: true,
          },
        );
      })
      .catch(() => {
        if (planningDatesRequestIdRef.current !== requestId) {
          return;
        }

        methods.setValue('plannedLoadDate', '', { shouldDirty: false, shouldTouch: false, shouldValidate: true });
        methods.setValue('transportationPlannedDate', '', { shouldDirty: false, shouldTouch: false, shouldValidate: true });
      });
  }, [calculatePlanningDates, isPickup, methods, requestedDeliveryDate, requestedPickupDate, routeCode]);

  useEffect(() => {
    const normalizedRouteCode = String(routeCode ?? '').trim();
    if (!normalizedRouteCode) {
      setSuggestedRequestedDate(null);
      return;
    }

    const timeZone = selectedSupplyPointTimeZone || order.supplyPointTimeZone || 'UTC';
    let plannedTransportationDate = new Date();
    plannedTransportationDate = new Date(plannedTransportationDate.toLocaleDateString('en-US', { timeZone }));

    const requestId = suggestedRequestedDateRequestIdRef.current + 1;
    suggestedRequestedDateRequestIdRef.current = requestId;

    void calculateDeliveryDate(normalizedRouteCode, plannedTransportationDate)
      .then((requestedDate) => {
        if (suggestedRequestedDateRequestIdRef.current !== requestId) {
          return;
        }

        const normalizedRequestedDate = normalizeDateForDateInput(requestedDate ?? '');
        const parsedRequestedDate = parseDateInputAsLocalDate(normalizedRequestedDate);
        if (Number.isNaN(parsedRequestedDate.getTime())) {
          setSuggestedRequestedDate(null);
          return;
        }

        setSuggestedRequestedDate(parsedRequestedDate);
      })
      .catch(() => {
        if (suggestedRequestedDateRequestIdRef.current !== requestId) {
          return;
        }

        setSuggestedRequestedDate(null);
      });
  }, [calculateDeliveryDate, order.supplyPointTimeZone, routeCode, selectedSupplyPointTimeZone]);

  useEffect(() => {
    const currentIncotermCode = normalizeLookupCode(String(incotermValue ?? ''));
    const previousIncotermCode = previousIncotermCodeRef.current;
    previousIncotermCodeRef.current = currentIncotermCode;

    if (!currentIncotermCode || !previousIncotermCode || currentIncotermCode === previousIncotermCode) {
      return;
    }

    const matchedPair = (incotermPairsQuery.data ?? []).find((pair) => {
      const pairCode = normalizeLookupCode(String(pair.inco1 ?? '').trim());
      return pairCode === currentIncotermCode;
    });

    methods.setValue('incotermLocation', String(matchedPair?.inco2 ?? '').trim(), {
      shouldDirty: true,
      shouldTouch: true,
      shouldValidate: true,
    });
  }, [incotermPairsQuery.data, incotermValue, methods]);

  useEffect(() => {
    const currentCode = String(notifyPartnerCodeValue ?? '').trim();
    const currentSnapshot = getNotifyPartnerSnapshot();

    if (!currentCode) {
      notifyPartnerSnapshotRef.current = currentSnapshot;
      return;
    }

    const selectedSnapshot = notifyPartnerSnapshotRef.current;
    const detailsChanged = currentSnapshot.name !== selectedSnapshot.name
      || currentSnapshot.addressLine1 !== selectedSnapshot.addressLine1
      || currentSnapshot.addressLine2 !== selectedSnapshot.addressLine2
      || currentSnapshot.city !== selectedSnapshot.city
      || currentSnapshot.state !== selectedSnapshot.state
      || currentSnapshot.country !== selectedSnapshot.country
      || currentSnapshot.postalCode !== selectedSnapshot.postalCode
      || currentSnapshot.email !== selectedSnapshot.email;

    if (!detailsChanged) {
      return;
    }

    methods.setValue('notifyPartnerCode', '', { shouldDirty: true, shouldValidate: true, shouldTouch: true });
    notifyPartnerSnapshotRef.current = { ...currentSnapshot, code: '' };
  }, [
    methods,
    notifyPartnerAddressLine1Value,
    notifyPartnerAddressLine2Value,
    notifyPartnerCityValue,
    notifyPartnerCodeValue,
    notifyPartnerCountryValue,
    notifyPartnerEmailValue,
    notifyPartnerNameValue,
    notifyPartnerPostalCodeValue,
    notifyPartnerStateValue,
  ]);

  useEffect(() => {
    const currentCode = String(consigneeCodeValue ?? '').trim();
    const currentSnapshot = getConsigneeSnapshot();

    if (!currentCode) {
      consigneeSnapshotRef.current = currentSnapshot;
      return;
    }

    const selectedSnapshot = consigneeSnapshotRef.current;
    const detailsChanged = currentSnapshot.name !== selectedSnapshot.name
      || currentSnapshot.addressLine1 !== selectedSnapshot.addressLine1
      || currentSnapshot.addressLine2 !== selectedSnapshot.addressLine2
      || currentSnapshot.city !== selectedSnapshot.city
      || currentSnapshot.state !== selectedSnapshot.state
      || currentSnapshot.country !== selectedSnapshot.country
      || currentSnapshot.postalCode !== selectedSnapshot.postalCode
      || currentSnapshot.email !== selectedSnapshot.email;

    if (!detailsChanged) {
      return;
    }

    methods.setValue('consigneeCode', '', { shouldDirty: true, shouldValidate: true, shouldTouch: true });
    consigneeSnapshotRef.current = { ...currentSnapshot, code: '' };
  }, [
    consigneeAddressLine1Value,
    consigneeAddressLine2Value,
    consigneeCityValue,
    consigneeCodeValue,
    consigneeCountryValue,
    consigneeEmailValue,
    consigneeNameValue,
    consigneePostalCodeValue,
    consigneeStateValue,
    methods,
  ]);

  useEffect(() => {
    const soldToNumber = String(soldToNumberValue ?? '').trim();
    const customerId = String(methods.getValues('customerId') ?? '').trim();

    if (soldToNumber && soldToNumber !== customerId) {
      methods.setValue('customerId', soldToNumber, { shouldDirty: false, shouldTouch: false, shouldValidate: true });
    }
  }, [methods, soldToNumberValue]);

  useEffect(() => {
    const hasCustomer = Boolean(String(soldToNumberValue ?? '').trim() || String(methods.getValues('customerId') ?? '').trim());
    if (!hasCustomer) {
      return;
    }

    const requestedDateField = isPickup === 'pickup' ? 'requestedPickupDate' : 'requestedDeliveryDate';
    const currentShippingConditionValue = String(methods.getValues('shippingCondition') ?? '');
    const currentRequestedDateValue = String(methods.getValues(requestedDateField) ?? '');

    methods.setValue('shippingCondition', currentShippingConditionValue, {
      shouldDirty: false,
      shouldTouch: true,
      shouldValidate: true,
    });
    methods.setValue(requestedDateField, currentRequestedDateValue, {
      shouldDirty: false,
      shouldTouch: true,
      shouldValidate: true,
    });

    void methods.trigger(['shippingCondition', requestedDateField]);
  }, [isPickup, methods, soldToNumberValue]);

  useEffect(() => {
    const supplyPointCode = String(currentSupplyPointCode ?? '').trim();
    const shipToNumber = String(currentShipToNumber ?? '').trim();
    const shippingConditionCode = String(currentShippingConditionCode ?? '').trim();
    const normalizedRouteCode = String(routeCode ?? '').trim().toLowerCase();

    if (!supplyPointCode || !shippingConditionCode || !shipToNumber) {
      methods.clearErrors('routeCode');
      setRouteDetailsError(false);
      return;
    }

    if (autoLoadRoutesQuery.isError) {
      setRouteDetailsError(true);
      return;
    }

    if (autoLoadRoutesQuery.isFetching) {
      setRouteDetailsError(false);
      return;
    }

    const routes = autoLoadRoutesQuery.data?.data ?? [];
    if (normalizedRouteCode) {
      const matchingRoute = routes.find((route) => {
        const candidate = String((route.routeCode ?? route.routeId ?? '')).trim().toLowerCase();
        return candidate === normalizedRouteCode;
      });

      if (matchingRoute) {
        setRouteFields(matchingRoute, false);
        return;
      }
    }

    if (routes.length > 0) {
      setRouteFields(routes[0], false);
    } else {
      clearRouteFields(false);
      methods.setError('routeCode', {
        type: 'required',
        message: 'Route code is required',
      });
    }
  }, [
    autoLoadRoutesQuery.data,
    autoLoadRoutesQuery.isError,
    autoLoadRoutesQuery.isFetching,
    currentSupplyPointCode,
    currentShippingConditionCode,
    currentShipToNumber,
    methods,
    routeCode,
  ]);

  useEffect(() => {
    const orderTypes = orderTypeOptions;
    if (orderTypes.length === 0) {
      return;
    }

    const currentOrderType = String(methods.getValues('orderType') ?? '').trim();
    if (!currentOrderType) {
      return;
    }

    const hasCode = orderTypes.some((ot) => ot.orderTypeCode.toUpperCase() === currentOrderType.toUpperCase());
    if (hasCode) {
      const selectedOption = orderTypes.find((ot) => ot.orderTypeCode.toUpperCase() === currentOrderType.toUpperCase());
      const selectedDescription = String(selectedOption?.orderTypeDescription ?? '').trim();
      if (selectedDescription) {
        methods.setValue('orderTypeDescription', selectedDescription, { shouldDirty: false, shouldTouch: false, shouldValidate: false });
      }
      return;
    }

    const matchedOption = orderTypes.find((ot) => {
      const description = String(ot.orderTypeDescription ?? '').trim();
      const composed = `${ot.orderTypeCode} - ${description}`;
      return description === currentOrderType || composed === currentOrderType;
    });

    if (matchedOption) {
      methods.setValue('orderType', matchedOption.orderTypeCode, { shouldDirty: false, shouldTouch: false, shouldValidate: true });
      methods.setValue('orderTypeDescription', String(matchedOption.orderTypeDescription ?? '').trim(), {
        shouldDirty: false,
        shouldTouch: false,
        shouldValidate: false,
      });
    }
  }, [methods, orderTypeOptions]);

  const shouldValidateTransportationDates = String(orderStatus ?? '') === 'OC-Draft' || String(orderStatus ?? '') === 'OC-New';
  const shouldValidateDischargeDate = String(orderStatus ?? '') === 'OC-Draft' || String(orderStatus ?? '') === 'OC-New';
  const customerExists = Boolean(methods.watch('soldToNumber') || methods.watch('customerId'));
  const customerDetailsVisible = customerExists || Boolean(
    String(currentShipToNumber ?? '').trim()
    || String(methods.watch('shipToName') ?? '').trim()
    || String(methods.watch('salesOrg') ?? '').trim()
    || String(currentSupplyPointId ?? currentSupplyPointCode ?? '').trim(),
  );
  const consigneeExists = Boolean(methods.watch('consigneeCode'));
  const shippingCondition = methods.watch('shippingCondition');
  const modeOfTransportation = methods.watch('modeOfTransportation');
  const expectedDischargeDate = methods.watch('expectedDischargeDate');

  const getEarliestDischargeDate = () => {
    const timeZone = selectedSupplyPointTimeZone || order.supplyPointTimeZone || 'UTC';
    let today = new Date();
    today = new Date(today.toLocaleDateString('en-US', { timeZone }));
    today.setDate(today.getDate() + 1);
    return today;
  };

  const getRequestedDateMin = () => {
    const timeZone = selectedSupplyPointTimeZone || order.supplyPointTimeZone || 'UTC';
    let today = new Date();
    today = new Date(today.toLocaleDateString('en-US', { timeZone }));
    return today;
  };

  const getEarliestRequestedDate = () => {
    const timeZone = selectedSupplyPointTimeZone || order.supplyPointTimeZone || 'UTC';

    if (suggestedRequestedDate && !Number.isNaN(suggestedRequestedDate.getTime())) {
      return suggestedRequestedDate;
    }

    let earliestDate = new Date();
    earliestDate = new Date(earliestDate.toLocaleDateString('en-US', { timeZone }));
    earliestDate.setDate(earliestDate.getDate() + 1);
    return earliestDate;
  };

  const earliestDischargeDate = getEarliestDischargeDate();
  const earliestDischargeDateDisplay = earliestDischargeDate.toLocaleDateString('en-US', {
    month: '2-digit',
    day: '2-digit',
    year: 'numeric',
  });
  const requestedDateMinInput = formatLocalDateForInput(getRequestedDateMin());
  const earliestRequestedDate = getEarliestRequestedDate();
  const earliestRequestedDateDisplay = earliestRequestedDate.toLocaleDateString('en-US', {
    month: '2-digit',
    day: '2-digit',
    year: 'numeric',
  });
  const expectedDischargeDateHasPassed = Boolean(
    shouldValidateDischargeDate
    && expectedDischargeDate
    && parseDateInputAsLocalDate(expectedDischargeDate) < earliestDischargeDate,
  );
  const notifyPartnerExists = Boolean(String(methods.watch('notifyPartnerCode') ?? '').trim() || String(methods.watch('notifyPartnerName') ?? '').trim());

  const clearConsignee = () => {
    clearConsigneeFields();
  };

  const movementType = useMemo(() => {
    const normalizedShippingCondition = normalizeLookupCode(String(shippingCondition ?? '').trim());
    const shippingConditions = (shippingConditionsQuery.data ?? []) as Array<{
      shippingConditionId?: string;
      code?: string;
      modeOfTransportation?: string;
      description?: string;
    }>;

    const matchedShippingCondition = shippingConditions.find((condition) => {
      const conditionId = normalizeLookupCode(String(condition.shippingConditionId ?? condition.code ?? '').trim());
      return conditionId === normalizedShippingCondition;
    });

    return resolveMovementType(
      matchedShippingCondition?.modeOfTransportation
      ?? modeOfTransportation
      ?? matchedShippingCondition?.description
      ?? '',
    );
  }, [shippingCondition, modeOfTransportation, shippingConditionsQuery.data]);

  useEffect(() => {
    const previousMovementType = previousMovementTypeRef.current;
    previousMovementTypeRef.current = movementType;

    if (!previousMovementType || previousMovementType === movementType) {
      return;
    }

    const setMovementFieldValue = (fieldName: keyof OrderFormValues, value = '') => {
      methods.setValue(fieldName, value as OrderFormValues[keyof OrderFormValues], {
        shouldDirty: true,
        shouldValidate: true,
        shouldTouch: false,
      });
    };

    const clearTruckOnlyFields = () => {
      setMovementFieldValue('numberOfTrucks');
    };

    const clearRailOnlyFields = () => {
      setMovementFieldValue('uniqueRCIDNumber');
      setMovementFieldValue('shipmentId');
      setMovementFieldValue('freightPayerNumber');
    };

    const clearMctOnlyFields = () => {
      setMovementFieldValue('containerType');
      setMovementFieldValue('numberOfContainers');
    };

    const clearMarineSharedFields = () => {
      setMovementFieldValue('loadLocationId');
      setMovementFieldValue('dischargeLocationId');
    };

    const clearMarineBulkOnlyFields = () => {
      setMovementFieldValue('vesselName');
      setMovementFieldValue('expectedDischargeDate');
      setMovementFieldValue('overrideLoadDate');
      setMovementFieldValue('surveyInspector');
      setMovementFieldValue('vesselScreeningNumber');
    };

    if (movementType === 'Truck') {
      clearRailOnlyFields();
      clearMctOnlyFields();
      clearMarineSharedFields();
      clearMarineBulkOnlyFields();
      clearConsignee();
      return;
    }

    if (movementType === 'Rail') {
      clearTruckOnlyFields();
      clearMctOnlyFields();
      clearMarineSharedFields();
      clearMarineBulkOnlyFields();
      clearConsignee();
      return;
    }

    if (movementType === 'MCT') {
      clearTruckOnlyFields();
      clearRailOnlyFields();
      clearMarineBulkOnlyFields();
      return;
    }

    if (movementType === 'MBL') {
      clearTruckOnlyFields();
      clearRailOnlyFields();
      clearMctOnlyFields();
      return;
    }

    clearTruckOnlyFields();
    clearRailOnlyFields();
    clearMctOnlyFields();
    clearMarineSharedFields();
    clearMarineBulkOnlyFields();
    clearConsignee();
  }, [methods, movementType]);

  const isTruckMovement = movementType === 'Truck';
  const isRailMovement = movementType === 'Rail';
  const isMctMovement = movementType === 'MCT';
  const isMarineBulkMovement = movementType === 'MBL';
  const hasMovementDetails = isTruckMovement || isRailMovement || isMctMovement || isMarineBulkMovement;

  const noProducts = !products || products.length === 0;
  // Use already-watched reactive values (methods.watch) — NOT getValues() which is non-reactive
  const noCustomer = !String(currentShipToNumber ?? '').trim();
  const noSupplyPoint = !String(currentSupplyPointId ?? currentSupplyPointCode ?? '').trim();

  const hasPendingChanges = methods.formState.isDirty || deletedProducts.length > 0;
  const formPristine = !hasPendingChanges;

  const isSubmitDisabled =
    formPristine
    || !methods.formState.isValid
    || noProducts
    || noCustomer
    || noSupplyPoint;

  if (typeof window !== 'undefined' && (window as any).__DEBUG_ORDER_FORM) {
    console.log('🔍 isSubmitDisabled breakdown:', {
      isDirty: methods.formState.isDirty,
      deletedProductsCount: deletedProducts.length,
      touchedFieldsCount: Object.keys(methods.formState.touchedFields).length,
      isValid: methods.formState.isValid,
      hasPendingChanges,
      formPristine,
      shipToNumber: currentShipToNumber,
      supplyPointId: currentSupplyPointId,
      supplyPointCode: currentSupplyPointCode,
      productsCount: products?.length ?? 0,
      noProducts,
      noCustomer,
      noSupplyPoint,
      isSubmitDisabled,
    });
  }

  useEffect(() => {
    if (saveDisabledAfterSave && hasPendingChanges) {
      setSaveDisabledAfterSave(false);
    }
  }, [hasPendingChanges, saveDisabledAfterSave]);
  const headerOrderNumber = order.orderNumber ?? order.orderId ?? '';
  const isNewOrder = !headerOrderNumber;
  const headerTitle = isNewOrder ? 'New Order' : `Order #${headerOrderNumber}`;

  return (
    <FormProvider {...methods}>
      <ThemeProvider theme={orderFormLineFieldTheme}>
      <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box
        className="page-header"
        sx={{ justifyContent: 'space-between', boxSizing: 'border-box' }}
      >
        <IconButton
          id={isNewOrder ? 'qa-discard-btn' : 'qa-back-btn'}
          onClick={() => {
            if (!skipDiscardPrompt && (isNewOrder || !formPristine)) {
              setDiscardDialogOpen(true);
            } else {
              onBack?.();
            }
          }}
          sx={{ color: '#ffffff' }}
        >
          {isNewOrder ? <ClearIcon /> : <ArrowBackIcon />}
        </IconButton>
        <Typography
          className="page-header-title"
          component="h1"
          sx={{
            color: '#ffffff',
          }}
        >
          {headerTitle}
        </Typography>
        <Box className="fill-space" />
        <Button
          type="button"
          onClick={() => {
            if (!isSubmitDisabled && !isSaving) {
              setSkipDiscardPrompt(true);
              setSaveDisabledAfterSave(true);
              void methods.handleSubmit(onSubmit)();
            }
          }}
          variant="contained"
          className="white-button"
          disabled={isSubmitDisabled || isSaving || saveDisabledAfterSave}
          sx={{
            minWidth: 140,
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            backgroundColor: '#ffffff',
            '& .MuiButton-startIcon, & svg': {
              color: '#000000 !important',
            },
            '&:hover': {
              backgroundColor: '#ffffff',
              color: '#000000 !important',
            },
            '&.Mui-disabled': {
              backgroundColor: '#ffffff',
              color: 'rgba(0, 0, 0, 0.38) !important',
              opacity: 1,
              '& .MuiButton-startIcon, & svg, & span': {
                color: 'rgba(0, 0, 0, 0.38) !important',
              },
            },
          }}
        >
          <SaveAltIcon sx={{ fontSize: '18px', color: 'inherit' }} />
          <span style={{ color: 'inherit' }}>
            {isSaving ? (isNewOrder ? 'SUBMITTING ORDER' : 'SAVING ORDER') : (isNewOrder ? 'SUBMIT ORDER' : 'SAVE ORDER')}
          </span>
        </Button>
      </Box>

      <Box
        sx={{ mt: 0, mx: '15px', mb: '15px' }}
        component="form"
        id={formId}
        onSubmit={methods.handleSubmit(onSubmit)}
        onChangeCapture={() => {
          if (saveDisabledAfterSave) {
            setSaveDisabledAfterSave(false);
          }

          if (skipDiscardPrompt) {
            setSkipDiscardPrompt(false);
          }
        }}
      >

        <Box sx={{ px: 3, pt: 3, pb: 2 }}>

        <Card variant="outlined" sx={{ mb: 2 }}>
          <CardHeader title="General Information" />
          <CardContent sx={{ pt: 3 }}>
        <Grid2 container spacing={4.5}>
          <Grid2 size={{ xs: 12, md: 3 }}>
            <Stack spacing={2}>
              <Controller
                name="orderNumber"
                control={methods.control}
                render={({ field }) => (
                  <TextField {...field} fullWidth size="small" label="Order Number" InputProps={{ readOnly: true }} />
                )}
              />
              <Controller
                name="creatorId"
                control={methods.control}
                render={({ field }) => (
                  <TextField {...field} fullWidth size="small" label="Order Creator" InputProps={{ readOnly: true }} />
                )}
              />
              <Controller
                name="orderPlacedDate"
                control={methods.control}
                render={({ field }) => (
                  <DatePicker
                    label="Order Creation Date"
                    value={field.value ? dayjs(field.value) : null}
                    format="MM/DD/YYYY"
                    onChange={(value) => field.onChange(value ? value.format('YYYY-MM-DD') : '')}
                    readOnly
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        size: 'small',
                        variant: 'standard',
                        id: 'qa-orderDateTimeStamp-ipt',
                      },
                    }}
                  />
                )}
              />
            </Stack>
          </Grid2>

          <Grid2 size={{ xs: 12, md: 3 }}>
            <Stack spacing={2}>
              <Controller
                name="status"
                control={methods.control}
                render={({ field }) => (
                  <TextField {...field} fullWidth select size="small" label="Order Status">
                    {orderStatusOptions.map((option) => (
                      <MenuItem key={option} value={option} disabled={!/^OC-/.test(option)}>{option}</MenuItem>
                    ))}
                  </TextField>
                )}
              />
              <Controller
                name="orderType"
                control={methods.control}
                rules={{ required: 'Order Type is required' }}
                render={({ field }) => (
                  <TextField {...field} fullWidth select size="small" label="Order Type*"
                    error={!!methods.formState.errors.orderType}
                    helperText={methods.formState.errors.orderType?.message}
                  >
                    {orderTypeOptions.map((ot) => (
                      <MenuItem key={ot.orderTypeCode} value={ot.orderTypeCode}>{ot.orderTypeDescription}</MenuItem>
                    ))}
                  </TextField>
                )}
              />
            </Stack>
          </Grid2>

          <Grid2 size={{ xs: 12, md: 3 }}>
            <Stack spacing={2}>
              <Controller
                name="responsibleEmployeeName"
                control={methods.control}
                render={({ field }) => (
                  <TextField {...field} fullWidth size="small" label="Responsible Party" InputProps={{ readOnly: true }} />
                )}
              />
              <Controller
                name="customerConfirmation"
                control={methods.control}
                render={({ field }) => (
                  <FormControlLabel
                    control={<Checkbox
                      checked={!!field.value}
                      onChange={(event) => {
                        const checked = event.target.checked;
                        field.onChange(checked);
                        methods.setValue('customerConfirmation', checked, {
                          shouldDirty: true,
                          shouldValidate: true,
                          shouldTouch: true,
                        });
                      }}
                    />}
                    label="Customer Confirmation"
                    id="qa-customerConfirmation-chb"
                  />
                )}
              />
            </Stack>
          </Grid2>

          <Grid2 size={{ xs: 12, md: 3 }}>
            <Stack spacing={2}>
              <Controller
                name="preCyberStatus"
                control={methods.control}
                render={({ field }) => (
                  <TextField {...field} fullWidth size="small" label="Pre-Incident Order Block" InputProps={{ readOnly: true }} />
                )}
              />
              <Controller
                name="preCyberOrderNumber"
                control={methods.control}
                render={({ field }) => (
                  <TextField {...field} fullWidth size="small" label="Pre-Incident Order Number" InputProps={{ readOnly: true }} />
                )}
              />
            </Stack>
          </Grid2>
        </Grid2>
          </CardContent>
        </Card>

        <Card variant="outlined" sx={{ mb: 2, p: '36px' }}>
          <CardHeader title="Customer Details" sx={{ pb: 0, pt: 0, px: 0 }} />
          <CardContent sx={{ p: '0 !important', pt: '24px !important' }}>
        <Grid2 container spacing={{ xs: 1, md: 4.5 }}>
          {!customerDetailsVisible && (
            <>
              <Grid2 size={{ xs: 12, md: 3 }}>
                <Button
                  id="qa-selectCustomerAndShipTo-btn"
                  variant="contained"
                  sx={{
                    px: 2,
                    py: 1,
                    lineHeight: 1.2,
                    width: { xs: '100%', md: 230 },
                    minHeight: 52,
                    color: '#0c479d !important',
                    backgroundColor: '#ffffff !important',
                    border: '1px solid #d9d9d9',
                    boxShadow: 'none',
                    '&:hover': {
                      backgroundColor: '#ffffff !important',
                      border: '1px solid #d9d9d9',
                      boxShadow: 'none',
                    },
                  }}
                  onClick={handleOpenCustomerDialog}
                >
                  <span style={{ textAlign: 'center' }}>
                    SELECT CUSTOMER AND<br />SHIP-TO
                  </span>
                </Button>
              </Grid2>
              <Grid2 size={{ xs: 12, md: 3 }} />
              <Grid2 size={{ xs: 12, md: 3 }} />
              <Grid2 size={{ xs: 12, md: 3 }} />
            </>
          )}

          {customerDetailsVisible && (
            <>
              <Grid2 size={{ xs: 12, md: 3 }}>
                <Stack spacing={{ xs: 1.5, md: 2.5 }} sx={{ width: '100%', minWidth: 0 }}>
                  <Button
                    id="qa-changeCustomerAndShipTo-btn"
                    variant="contained"
                    sx={{
                      px: 2,
                      py: 1,
                      lineHeight: 1.2,
                      width: { xs: '100%', md: 230 },
                      minHeight: 52,
                      color: '#0c479d !important',
                      backgroundColor: '#ffffff !important',
                      border: '1px solid #d9d9d9',
                      boxShadow: 'none',
                      '&:hover': {
                        backgroundColor: '#ffffff !important',
                        border: '1px solid #d9d9d9',
                        boxShadow: 'none',
                      },
                    }}
                    onClick={handleOpenCustomerDialog}
                  >
                    <span style={{ textAlign: 'center' }}>
                      CHANGE CUSTOMER AND<br />SHIP-TO
                    </span>
                  </Button>

                  <Box sx={{ width: '100%', minWidth: 0 }}>
                    <Controller
                      name="shippingCondition"
                      control={methods.control}
                      rules={{ required: 'Shipping Condition is required' }}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          fullWidth
                          size="small"
                          label="Shipping Condition"
                          id="qa-shippingCondition-ipt"
                          className="mouse-pointer"
                          inputProps={{ readOnly: true, style: { cursor: 'pointer' } }}
                          InputProps={{
                            endAdornment: (
                              <InputAdornment
                                position="end"
                                sx={{ cursor: 'pointer' }}
                                onClick={openShippingConditionDialog}
                              >
                                <ArrowDropDownIcon />
                              </InputAdornment>
                            ),
                          }}
                          onClick={openShippingConditionDialog}
                          error={!!methods.formState.errors.shippingCondition}
                          helperText={methods.formState.errors.shippingCondition?.message}
                        />
                      )}
                    />
                  </Box>

                  <Controller
                    name="incoterm"
                    control={methods.control}
                    render={({ field }) => {
                      const options = (incotermsQuery.data ?? []).map((term) => {
                        const code = getIncotermLookupCode(term);
                        const description = getIncotermLookupDescription(term);
                        return code && description ? `${code} - ${description}` : code;
                      }).filter((option): option is string => Boolean(String(option ?? '').trim()));
                      const currentValue = String(field.value ?? '').trim();
                      const hasCurrentValueOption = options.includes(currentValue);

                      return (
                        <TextField
                          {...field}
                          fullWidth
                          select
                          size="small"
                          label="Incoterms"
                          id="qa-incoterms-sel"
                          SelectProps={{
                            renderValue: (selected) => String(selected ?? ''),
                          }}
                        >
                          {currentValue && !hasCurrentValueOption ? (
                            <MenuItem key={currentValue} value={currentValue}>
                              {currentValue}
                            </MenuItem>
                          ) : null}
                          {options.map((option) => (
                            <MenuItem key={option} value={option}>
                              {option}
                            </MenuItem>
                          ))}
                        </TextField>
                      );
                    }}
                  />

                  <Controller
                    name="incotermLocation"
                    control={methods.control}
                    render={({ field }) => (
                      <TextField {...field} fullWidth size="small" label="Incoterms 2" id="qa-incoterms2-ipt" />
                    )}
                  />

                  <Controller
                    name="purchaseOrderNumber"
                    control={methods.control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        fullWidth
                        size="small"
                        label="Purchase Order #"
                        id="qa-purchaseOrderNumber-ipt"
                      />
                    )}
                  />

                  <Controller
                    name="contactName"
                    control={methods.control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        fullWidth
                        size="small"
                        label="Contact Name"
                        id="qa-contactName-ipt"
                      />
                    )}
                  />

                  <Controller
                    name="contactEmail"
                    control={methods.control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        fullWidth
                        size="small"
                        label="Email Address"
                        id="qa-emailAddress-ipt"
                      />
                    )}
                  />

                  <Controller
                    name="contactPhoneNumber"
                    control={methods.control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        fullWidth
                        size="small"
                        label="Telephone Number"
                        id="qa-telephoneNumber-ipt"
                      />
                    )}
                  />
                </Stack>
              </Grid2>

              <Grid2 size={{ xs: 12, md: 3 }}>
            <Stack spacing={2.5} sx={{ width: '100%', minWidth: 0 }}>
              <Controller
                name="soldToNumber"
                control={methods.control}
                render={({ field }) => (
                  <TextField {...field} fullWidth size="small" label="Sold-to Code" id="qa-soldToNumber-ipt" InputProps={{ readOnly: true }} />
                )}
              />

              <Controller
                name="customerName"
                control={methods.control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    size="small"
                    label="Sold-to Name"
                    id="qa-customerSTOName-ipt"
                    InputProps={{ readOnly: true }}
                  />
                )}
              />

              <Controller
                name="criticalCustomer"
                control={methods.control}
                render={({ field }) => (
                  <FormControlLabel
                    control={<Checkbox
                      checked={!!field.value}
                      onChange={(event) => {
                        const checked = event.target.checked;
                        field.onChange(checked);
                        methods.setValue('criticalCustomer', checked, {
                          shouldDirty: true,
                          shouldValidate: true,
                          shouldTouch: true,
                        });
                      }}
                    />}
                    label="Critical Customer"
                    id="qa-criticalCustomer-chb"
                  />
                )}
              />

              <TextField
                fullWidth
                size="small"
                variant="standard"
                InputProps={{ readOnly: true }}
                sx={{ visibility: 'hidden' }}
              />

              <Controller
                name="salesOrg"
                control={methods.control}
                render={({ field }) => (
                  <TextField {...field} fullWidth size="small" label="Org" id="qa-sellingSalesOrgNumber-ipt" InputProps={{ readOnly: true }} />
                )}
              />

              <Controller
                name="distributionChannel"
                control={methods.control}
                render={({ field }) => (
                  <TextField {...field} fullWidth size="small" label="Dist. Channel" id="qa-distChannel-ipt" InputProps={{ readOnly: true }} />
                )}
              />

              <Controller
                name="division"
                control={methods.control}
                render={({ field }) => (
                  <TextField {...field} fullWidth size="small" label="Division" id="qa-division-ipt" InputProps={{ readOnly: true }} />
                )}
              />

              <Stack direction="row" spacing={1} alignItems="center">
                <Box sx={{ flex: '0 0 180px' }}>
                  <Controller
                    name="freightForwarder"
                    control={methods.control}
                    render={({ field }) => (
                      <TextField {...field} fullWidth size="small" label="Freight Forwarder" id="qa-freightForwarder-ipt" inputProps={{ maxLength: 10 }} />
                    )}
                  />
                </Box>
                <IconButton id="qa-editFreightForwarder-btn" size="small" onClick={() => setFreightForwarderDialogOpen(true)}>
                  <EditIcon fontSize="small" sx={{ color: '#0c479d' }} />
                </IconButton>
                <IconButton id="qa-deleteFreightForwarder-btn" size="small" onClick={() => methods.setValue('freightForwarder', '', { shouldDirty: true, shouldValidate: true, shouldTouch: true })}>
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Stack>

              <Stack direction="row" spacing={1} alignItems="flex-start">
                <Box sx={{ flex: '0 0 220px' }}>
                  <Controller
                    name="drcCode"
                    control={methods.control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        fullWidth
                        size="small"
                        label="DRC Code"
                        id="qa-drcCode-ipt"
                        InputProps={{ readOnly: true }}
                      />
                    )}
                  />
                  <Controller
                    name="drcCodeDesc"
                    control={methods.control}
                    render={({ field }) => (
                      <TextField {...field} fullWidth size="small" label="DRC Desc" id="qa-drcCodeDesc-ipt" InputProps={{ readOnly: true }} sx={{ mt: 2 }} />
                    )}
                  />
                </Box>
                <IconButton id="qa-editDrcCode-btn" size="small" onClick={() => setDrcCodeDialogOpen(true)} sx={{ mt: 0.5 }}>
                  <EditIcon fontSize="small" sx={{ color: '#0c479d' }} />
                </IconButton>
                <IconButton id="qa-deleteDrcCode-btn" size="small" onClick={() => {
                  methods.setValue('drcCode', '', { shouldDirty: true, shouldValidate: true, shouldTouch: true });
                  methods.setValue('drcCodeDesc', '', { shouldDirty: true, shouldValidate: true, shouldTouch: true });
                  methods.setValue('drcCodeCountry', '', { shouldDirty: true, shouldValidate: true, shouldTouch: true });
                  methods.setValue('drcCodeRegion', '', { shouldDirty: true, shouldValidate: true, shouldTouch: true });
                }} sx={{ mt: 0.5 }}>
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Stack>
            </Stack>
              </Grid2>

              <Grid2 size={{ xs: 12, md: 3 }}>
            <Stack spacing={2.5} sx={{ width: '100%', minWidth: 0 }}>
              <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '1.125rem', lineHeight: 1.5, pb: '4px' }}>
                Delivery Address
              </Typography>

              <Controller
                name="shipToNumber"
                control={methods.control}
                render={({ field }) => (
                  <TextField {...field} fullWidth size="small" label="Ship-to Code" id="qa-shipToPlantCodeNumber-ipt" InputProps={{ readOnly: true }} />
                )}
              />

              <Controller
                name="shipToName"
                control={methods.control}
                render={({ field }) => (
                  <TextField {...field} fullWidth size="small" label="Ship-to Name" id="qa-shipToName-ipt" InputProps={{ readOnly: true }} />
                )}
              />

              <AddressFields<OrderFormValues>
                control={methods.control}
                disabled
                fieldNames={{
                  addressLine1: 'shipToAddressLine1',
                  addressLine2: 'shipToAddressLine2',
                  city: 'shipToCity',
                  state: 'shipToState',
                  country: 'shipToCountry',
                  postalCode: 'shipToPostalCode',
                  email: 'shipToEmail',
                }}
              />
            </Stack>
              </Grid2>

              <Grid2 size={{ xs: 12, md: 3 }}>
            <Stack spacing={2.5} sx={{ width: '100%', minWidth: 0 }}>
              <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '1.125rem', lineHeight: 1.5 }}>
                Notify Partner (Optional)
              </Typography>

              {!isNewOrder ? (
                <>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Box sx={{ flex: 1 }}>
                      <Controller
                        name="notifyPartnerCode"
                        control={methods.control}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            fullWidth
                            size="small"
                            label="Notify Partner Code"
                            id="qa-notifyPartnerCode-ipt"
                            InputProps={{ readOnly: true }}
                            disabled={disableNotifyPartnerModal}
                          />
                        )}
                      />
                    </Box>
                    {notifyPartnerExists ? (
                      <>
                        <IconButton
                          id="qa-editNotifyPartner-btn"
                          size="small"
                          onClick={openNotifyPartnerDialog}
                          disabled={disableNotifyPartnerModal}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          id="qa-deleteNotifyPartner-btn"
                          size="small"
                          onClick={() => clearNotifyPartnerFields()}
                          disabled={disableNotifyPartnerModal}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </>
                    ) : null}
                  </Stack>
                </>
              ) : null}

              <Controller
                name="notifyPartnerName"
                control={methods.control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    size="small"
                    label="Notify Partner Name"
                    id="qa-notifyPartnerName-ipt"
                    InputProps={{ readOnly: disableNotifyPartnerModal }}
                  />
                )}
              />

              <AddressFields<OrderFormValues>
                control={methods.control}
                disabled={disableNotifyPartnerModal}
                fieldNames={{
                  addressLine1: 'notifyPartnerAddressLine1',
                  addressLine2: 'notifyPartnerAddressLine2',
                  city: 'notifyPartnerCity',
                  state: 'notifyPartnerState',
                  country: 'notifyPartnerCountry',
                  postalCode: 'notifyPartnerPostalCode',
                  email: 'notifyPartnerEmail',
                }}
              />
            </Stack>
              </Grid2>
            </>
          )}
        </Grid2>
          </CardContent>
        </Card>

        </Box>

        {customerDetailsVisible && (
        <Box sx={{ px: 3, pb: 2 }}>
        <OrderDetailsCard
          supplyPointsLoading={supplyPointsQuery.isLoading}
          routesLoading={autoLoadRoutesQuery.isFetching}
          supplyPointSelected={!!methods.getValues('supplyPointCode')}
          shouldValidateTransportationDates={shouldValidateTransportationDates}
          routeDetailsError={routeDetailsError}
          earliestRequestedDate={earliestRequestedDateDisplay}
          requestedDateMinInput={requestedDateMinInput}
          onSelectSupplyPoint={handleSelectSupplyPoint}
          onDeleteSupplyPoint={handleDeleteSupplyPoint}
          onSelectRoute={handleSelectRoute}
          onOpenSupplyPointDialog={() => setSupplyPointDialogOpen(true)}
          onOpenRouteDialog={() => setRouteDialogOpen(true)}
          onRetryRouteDetails={() => {
            void autoLoadRoutesQuery.refetch();
          }}
          onSetEarliestRequestedDate={() => {
            const fieldName = methods.getValues('isPickup') === 'pickup'
              ? 'requestedPickupDate'
              : 'requestedDeliveryDate';
            const requestedDate = formatLocalDateForInput(earliestRequestedDate);

            methods.setValue(fieldName, requestedDate, {
              shouldDirty: true,
              shouldValidate: true,
              shouldTouch: true,
            });
          }}
        />
        </Box>
        )}

        <Box sx={{ px: 3, pb: 2 }}>

        {hasMovementDetails ? (
        <Card variant="outlined" sx={{ mb: 2 }}>
          <CardHeader
            title={isTruckMovement ? 'Truck Details' : isRailMovement ? 'Rail Details' : isMctMovement ? 'Marine Container Transport Details' : 'Marine Bulk Details'}
            subheader={(
              <span>
                Shipping condition is selected in customer details above.
                {' '}
                <a
                  id="qa-changeMovementType-lnk"
                  href="#"
                  onClick={(event) => {
                    event.preventDefault();
                    openShippingConditionDialog();
                  }}
                >
                  Change shipping condition
                </a>
              </span>
            )}
          />
          <CardContent sx={{ pt: 3 }}>
        {isTruckMovement ? (
          <Grid2 container spacing={4.5}>
            <Grid2 size={{ xs: 12, md: 3 }}>
              <Controller
                name="numberOfTrucks"
                control={methods.control}
                render={({ field }) => (
                  <TextField {...field} fullWidth size="small" label="Number of Trucks" type="number" inputProps={{ min: 0 }} id="qa-numberOfTrucks-ipt" />
                )}
              />
            </Grid2>
            <Grid2 size={{ xs: 12, md: 3 }} />
            <Grid2 size={{ xs: 12, md: 3 }} />
            <Grid2 size={{ xs: 12, md: 3 }} />
          </Grid2>
        ) : null}

        {isRailMovement ? (
          <Grid2 container spacing={4.5}>
            <Grid2 size={{ xs: 12, md: 3 }}>
              <Stack spacing={2}>
                <Controller
                  name="uniqueRCIDNumber"
                  control={methods.control}
                  render={({ field }) => (
                    <TextField {...field} fullWidth size="small" label="Unique RC ID #" id="qa-uniqueRCIDNumber-ipt" />
                  )}
                />
                <Controller
                  name="shipmentId"
                  control={methods.control}
                  render={({ field }) => (
                    <TextField {...field} fullWidth size="small" label="Shipment ID #" id="qa-shipmentId-ipt" />
                  )}
                />
              </Stack>
            </Grid2>
            <Grid2 size={{ xs: 12, md: 3 }}>
              <Controller
                name="freightPayerNumber"
                control={methods.control}
                render={({ field }) => (
                  <TextField {...field} fullWidth size="small" label="Payer of Freight" id="qa-freightPayerNumber-ipt" />
                )}
              />
            </Grid2>
            <Grid2 size={{ xs: 12, md: 3 }} />
            <Grid2 size={{ xs: 12, md: 3 }} />
          </Grid2>
        ) : null}

        {isMctMovement ? (
          <Grid2 container spacing={4.5}>
            <Grid2 size={{ xs: 12, md: 3 }}>
              <Stack spacing={2}>
                <Controller
                  name="containerType"
                  control={methods.control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      fullWidth
                      size="small"
                      label="Container Type"
                      id="qa-containerType-ipt"
                      InputProps={{
                        readOnly: true,
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton edge="end" onClick={() => setContainerTypeDialogOpen(true)}>
                              <ArrowDropDownIcon />
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                      onClick={() => setContainerTypeDialogOpen(true)}
                    />
                  )}
                />
                <Controller
                  name="numberOfContainers"
                  control={methods.control}
                  rules={{ min: 0 }}
                  render={({ field, fieldState }) => (
                    <TextField
                      {...field}
                      fullWidth
                      size="small"
                      label="# of Containers"
                      type="number"
                      inputProps={{ min: 0 }}
                      id="qa-numberOfContainers-ipt"
                      error={!!fieldState.error}
                      helperText={fieldState.error?.type === 'min' ? 'Must be a positive integer' : undefined}
                    />
                  )}
                />
                <Controller
                  name="loadLocationId"
                  control={methods.control}
                  render={({ field }) => (
                    <TextField {...field} fullWidth size="small" label="Load Location / Origin Port" id="qa-loadLocationId-ipt" />
                  )}
                />
                <Controller
                  name="dischargeLocationId"
                  control={methods.control}
                  render={({ field }) => (
                    <TextField {...field} fullWidth size="small" label="Discharge Port" id="qa-dischargeLocationId-ipt" />
                  )}
                />
              </Stack>
            </Grid2>

            <Grid2 size={{ xs: 12, md: 3 }}>
              <Stack spacing={2}>
                <Typography variant="h6" sx={{ fontWeight: 400, fontSize: '0.9rem' }}>
                  Consignee
                </Typography>
                <Button id="qa-consignee-btn" variant="outlined" onClick={openConsigneeDialog}>
                  SELECT CONSIGNEE
                </Button>
                {consigneeExists ? (
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Controller
                      name="consigneeCode"
                      control={methods.control}
                      render={({ field }) => (
                        <TextField {...field} fullWidth size="small" label="Consignee Code" id="qa-consigneeCode-ipt" />
                      )}
                    />
                    <IconButton id="qa-editConsignee-btn" size="small" onClick={openConsigneeDialog}>
                      <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton id="qa-deleteConsignee-btn" size="small" onClick={clearConsignee}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Stack>
                ) : null}
                <Controller
                  name="consigneeName"
                  control={methods.control}
                  render={({ field }) => (
                    <TextField {...field} fullWidth size="small" label="Consignee Name" id="qa-consigneeName-ipt" />
                  )}
                />
                <Controller
                  name="consigneeAddressLine1"
                  control={methods.control}
                  render={({ field }) => (
                    <TextField {...field} fullWidth size="small" label="Address line 1" id="qa-consigneeAddressLine1-ipt" />
                  )}
                />
                <Controller
                  name="consigneeCity"
                  control={methods.control}
                  render={({ field }) => (
                    <TextField {...field} fullWidth size="small" label="City" id="qa-consigneeCity-ipt" />
                  )}
                />
              </Stack>
            </Grid2>

            <Grid2 size={{ xs: 12, md: 3 }}>
              <Stack spacing={2}>
                <Typography variant="h6" sx={{ fontWeight: 400, fontSize: '0.9rem', visibility: 'hidden' }}>
                  Consignee
                </Typography>
                <Controller
                  name="consigneeEmail"
                  control={methods.control}
                  render={({ field }) => (
                    <TextField {...field} fullWidth size="small" label="Email" id="qa-consigneeEmail-ipt" />
                  )}
                />
                <Controller
                  name="consigneeAddressLine2"
                  control={methods.control}
                  render={({ field }) => (
                    <TextField {...field} fullWidth size="small" label="Address line 2" id="qa-consigneeAddressLine2-ipt" />
                  )}
                />
                <Grid2 container spacing={1}>
                  <Grid2 size={{ xs: 4 }}>
                    <Controller
                      name="consigneeState"
                      control={methods.control}
                      render={({ field }) => (
                        <TextField {...field} fullWidth size="small" label="State/Prov" id="qa-consigneeStateProv-ipt" />
                      )}
                    />
                  </Grid2>
                  <Grid2 size={{ xs: 4 }}>
                    <Controller
                      name="consigneeCountry"
                      control={methods.control}
                      render={({ field }) => (
                        <TextField {...field} fullWidth size="small" label="Country" id="qa-consigneeCountry-ipt" />
                      )}
                    />
                  </Grid2>
                  <Grid2 size={{ xs: 4 }}>
                    <Controller
                      name="consigneePostalCode"
                      control={methods.control}
                      render={({ field }) => (
                        <TextField {...field} fullWidth size="small" label="Postal Code" id="qa-consigneePostalCode" />
                      )}
                    />
                  </Grid2>
                </Grid2>
              </Stack>
            </Grid2>

            <Grid2 size={{ xs: 12, md: 3 }} />
          </Grid2>
        ) : null}

        {isMarineBulkMovement ? (
        <Grid2 container spacing={4.5}>
          <Grid2 size={{ xs: 12, md: 3 }}>
            <Stack spacing={2}>
              <Controller
                name="vesselName"
                control={methods.control}
                render={({ field }) => (
                  <TextField {...field} fullWidth size="small" label="Vessel Name / Voyage #" id="qa-vesselName-ipt" />
                )}
              />
              <Controller
                name="expectedDischargeDate"
                control={methods.control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    type="date"
                    InputLabelProps={{ shrink: true }}
                    fullWidth
                    size="small"
                    label="Expected Discharge Date"
                    id="qa-expectedDischargeDate-ipt"
                    error={expectedDischargeDateHasPassed}
                    helperText={expectedDischargeDateHasPassed ? (
                      <span id="qa-expectedDischargeDate-errormsg">
                        Date has passed, change
                        {' '}
                        <a
                          id="qa-updateexpecteddischargedate-link"
                          href="#"
                          onClick={(event) => {
                            event.preventDefault();
                            event.stopPropagation();
                            methods.setValue('expectedDischargeDate', formatLocalDateForInput(earliestDischargeDate), {
                              shouldDirty: true,
                              shouldValidate: true,
                              shouldTouch: true,
                            });
                          }}
                        >
                          date to greater or equal to {earliestDischargeDateDisplay}
                        </a>
                      </span>
                    ) : undefined}
                  />
                )}
              />
              <Controller
                name="loadLocationId"
                control={methods.control}
                render={({ field }) => (
                  <TextField {...field} fullWidth size="small" label="Load Location ID" id="qa-loadLocationId-ipt" />
                )}
              />
              <Controller
                name="overrideLoadDate"
                control={methods.control}
                render={({ field }) => (
                  <TextField {...field} type="date" InputLabelProps={{ shrink: true }} fullWidth size="small" label="Override Load Date" id="qa-overrideLoadDate-ipt" />
                )}
              />
            </Stack>
          </Grid2>

          <Grid2 size={{ xs: 12, md: 3 }}>
            <Stack spacing={2}>
              <Controller
                name="surveyInspector"
                control={methods.control}
                render={({ field }) => (
                  <TextField {...field} fullWidth size="small" label="Inspection Co. / Surveyor's Info" id="qa-surveyInspector-ipt" />
                )}
              />
              <Controller
                name="vesselScreeningNumber"
                control={methods.control}
                render={({ field }) => (
                  <TextField {...field} fullWidth size="small" label="Vessel Screening Number" id="qa-vesselScreeningNumber-ipt" />
                )}
              />
              <Controller
                name="dischargeLocationId"
                control={methods.control}
                render={({ field }) => (
                  <TextField {...field} fullWidth size="small" label="Discharge Location ID" id="qa-dischargeLocationId-ipt" />
                )}
              />
            </Stack>
          </Grid2>

          <Grid2 size={{ xs: 12, md: 3 }}>
            <Stack spacing={2}>
              <Typography variant="h6" sx={{ fontWeight: 400, fontSize: '0.9rem' }}>
                Consignee
              </Typography>
              <Button id="qa-consignee-btn" variant="outlined" onClick={openConsigneeDialog}>
                SELECT CONSIGNEE
              </Button>
              {consigneeExists ? (
                <Stack direction="row" spacing={1} alignItems="center">
                  <Box sx={{ flex: 1 }}>
                    <Controller
                      name="consigneeCode"
                      control={methods.control}
                      render={({ field }) => (
                        <TextField {...field} fullWidth size="small" label="Consignee Code" id="qa-consigneeCode-ipt" />
                      )}
                    />
                  </Box>
                  <>
                    <IconButton id="qa-editConsignee-btn" size="small" onClick={openConsigneeDialog}>
                      <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton id="qa-deleteConsignee-btn" size="small" onClick={clearConsignee}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </>
                </Stack>
              ) : null}
              <Controller
                name="consigneeName"
                control={methods.control}
                render={({ field }) => (
                  <TextField {...field} fullWidth size="small" label="Consignee Name" id="qa-consigneeName-ipt" />
                )}
              />
              <Controller
                name="consigneeAddressLine1"
                control={methods.control}
                render={({ field }) => (
                  <TextField {...field} fullWidth size="small" label="Address line 1" id="qa-consigneeAddressLine1-ipt" />
                )}
              />
              <Controller
                name="consigneeAddressLine2"
                control={methods.control}
                render={({ field }) => (
                  <TextField {...field} fullWidth size="small" label="Address line 2" id="qa-consigneeAddressLine2-ipt" />
                )}
              />
              <Controller
                name="consigneeCity"
                control={methods.control}
                render={({ field }) => (
                  <TextField {...field} fullWidth size="small" label="City" id="qa-consigneeCity-ipt" />
                )}
              />
            </Stack>
          </Grid2>

          <Grid2 size={{ xs: 12, md: 3 }}>
            <Stack spacing={2}>
              <Typography variant="h6" sx={{ fontWeight: 400, fontSize: '0.9rem', visibility: 'hidden' }}>
                Consignee
              </Typography>
              <Controller
                name="consigneeState"
                control={methods.control}
                render={({ field }) => (
                  <TextField {...field} fullWidth size="small" label="State/Prov" id="qa-consigneeStateProv-ipt" />
                )}
              />
              <Controller
                name="consigneeCountry"
                control={methods.control}
                render={({ field }) => (
                  <TextField {...field} fullWidth size="small" label="Country" id="qa-consigneeCountry-ipt" />
                )}
              />
              <Controller
                name="consigneePostalCode"
                control={methods.control}
                render={({ field }) => (
                  <TextField {...field} fullWidth size="small" label="Postal Code" id="qa-consigneePostalCode" />
                )}
              />
              <Controller
                name="consigneeEmail"
                control={methods.control}
                render={({ field }) => (
                  <TextField {...field} fullWidth size="small" label="Email" id="qa-consigneeEmail-ipt" />
                )}
              />
            </Stack>
          </Grid2>
        </Grid2>
        ) : null}
          </CardContent>
        </Card>
        ) : null}

        <Card variant="outlined" sx={{ mb: 2 }}>
          <CardHeader title="Notes" />
          <CardContent sx={{ pt: 3 }}>
        <Grid2 container spacing={4.5}>
          <Grid2 size={{ xs: 12, md: 3 }}>
            <Controller
              name="haulierText"
              control={methods.control}
              render={({ field }) => (
                <TextField {...field} fullWidth size="small" label="Shipping / Haulier Text" multiline minRows={1} maxRows={6} />
              )}
            />
          </Grid2>

          <Grid2 size={{ xs: 12, md: 3 }}>
            <Controller
              name="specialInstructions"
              control={methods.control}
              render={({ field }) => (
                <TextField {...field} fullWidth size="small" multiline minRows={1} maxRows={6} label="Internal Notes" />
              )}
            />
          </Grid2>

          <Grid2 size={{ xs: 12, md: 3 }}>
            <Controller
              name="manualInvoiceNumber"
              control={methods.control}
              render={({ field }) => (
                <TextField {...field} fullWidth size="small" label="Manual Invoice #" />
              )}
            />
          </Grid2>

          <Grid2 size={{ xs: 12, md: 3 }} />
        </Grid2>
          </CardContent>
        </Card>

        </Box>

        <Box sx={{ px: 3, pb: 2 }}>
          <ProductTable
            onProductDeleted={(product) => {
              queueDeletedPersistedProducts([product]);
            }}
          />
        </Box>
      </Box>

      <SelectionDialog
        open={shippingConditionDialogOpen}
        title="Select a shipping condition"
        rows={shippingConditionRows}
        columns={shippingConditionColumns}
        onClose={() => {
          setShippingConditionDialogOpen(false);
          setShippingConditionSearchInput('');
        }}
        onSelect={handleSelectShippingCondition}
        loading={shippingConditionsQuery.isLoading}
        searchValue={shippingConditionSearchInput}
        onSearchValueChange={setShippingConditionSearchInput}
        onSearch={() => undefined}
        onClear={() => setShippingConditionSearchInput('')}
        paperStyle={{ width: '900px', minHeight: '500px' }}
      />

      <SelectionDialog
        open={customerDialogOpen}
        title="Select Ship-to Location"
        rows={customersQuery.data?.data ?? []}
        columns={customerColumns}
        onClose={() => {
          setCustomerDialogOpen(false);
          setCustomerPage(0);
        }}
        onSelect={handleSelectCustomer}
        loading={customersQuery.isLoading}
        searchValue={customerSearchInput}
        onSearchValueChange={setCustomerSearchInput}
        onSearch={() => {
          setCustomerPage(0);
          setCustomerSearchTerm(customerSearchInput.trim());
        }}
        onClear={() => {
          setCustomerSearchInput('');
          setCustomerSearchTerm('');
          setCustomerPage(0);
          setCustomerRowsPerPage(5);
        }}
        totalCount={customersQuery.data?.count ?? 0}
        page={customerPage}
        rowsPerPage={customerRowsPerPage}
        onPageChange={setCustomerPage}
        onRowsPerPageChange={setCustomerRowsPerPage}
        paperStyle={{ width: '1573px', minHeight: '500px' }}
      />

      <SelectionDialog
        open={freightForwarderDialogOpen}
        title="Select Freight Forwarder"
        rows={freightForwardersQuery.data?.data ?? []}
        columns={freightForwarderColumns}
        onClose={() => {
          setFreightForwarderDialogOpen(false);
          setFreightForwarderPage(0);
        }}
        onSelect={handleSelectFreightForwarder}
        loading={freightForwardersQuery.isLoading}
        searchValue={freightForwarderSearchInput}
        onSearchValueChange={setFreightForwarderSearchInput}
        onSearch={() => {
          setFreightForwarderPage(0);
          setFreightForwarderSearchTerm(freightForwarderSearchInput.trim());
        }}
        onClear={() => {
          setFreightForwarderSearchInput('');
          setFreightForwarderSearchTerm('');
          setFreightForwarderPage(0);
          setFreightForwarderRowsPerPage(5);
        }}
        totalCount={freightForwardersQuery.data?.count ?? 0}
        page={freightForwarderPage}
        rowsPerPage={freightForwarderRowsPerPage}
        onPageChange={setFreightForwarderPage}
        onRowsPerPageChange={setFreightForwarderRowsPerPage}
        paperStyle={{ width: '1573px', minHeight: '500px' }}
      />

      <SelectionDialog
        open={notifyPartnerDialogOpen}
        title="Select a notify partner"
        rows={notifyPartnerLookupQuery.data?.data ?? []}
        columns={notifyPartnerColumns}
        onClose={() => {
          setNotifyPartnerDialogOpen(false);
          setNotifyPartnerPage(0);
        }}
        onSelect={handleSelectNotifyPartner}
        loading={notifyPartnerLookupQuery.isLoading}
        searchValue={notifyPartnerSearchInput}
        onSearchValueChange={setNotifyPartnerSearchInput}
        onSearch={() => {
          setNotifyPartnerPage(0);
          setNotifyPartnerSearchTerm(notifyPartnerSearchInput.trim());
        }}
        onClear={() => {
          setNotifyPartnerSearchInput('');
          setNotifyPartnerSearchTerm('');
          setNotifyPartnerPage(0);
          setNotifyPartnerRowsPerPage(5);
        }}
        totalCount={notifyPartnerLookupQuery.data?.count ?? 0}
        page={notifyPartnerPage}
        rowsPerPage={notifyPartnerRowsPerPage}
        onPageChange={setNotifyPartnerPage}
        onRowsPerPageChange={setNotifyPartnerRowsPerPage}
        paperStyle={{ width: '1573px', minHeight: '500px' }}
      />

      <SelectionDialog
        open={consigneeDialogOpen}
        title="Select Consignee"
        rows={consigneeLookupQuery.data?.data ?? []}
        columns={consigneeColumns}
        onClose={() => {
          setConsigneeDialogOpen(false);
          setConsigneePage(0);
        }}
        onSelect={handleSelectConsignee}
        loading={consigneeLookupQuery.isLoading}
        searchValue={consigneeSearchInput}
        onSearchValueChange={setConsigneeSearchInput}
        onSearch={() => {
          setConsigneePage(0);
          setConsigneeSearchTerm(consigneeSearchInput.trim());
        }}
        onClear={() => {
          setConsigneeSearchInput('');
          setConsigneeSearchTerm('');
          setConsigneePage(0);
          setConsigneeRowsPerPage(5);
        }}
        totalCount={consigneeLookupQuery.data?.count ?? 0}
        page={consigneePage}
        rowsPerPage={consigneeRowsPerPage}
        onPageChange={setConsigneePage}
        onRowsPerPageChange={setConsigneeRowsPerPage}
        paperStyle={{ width: '1573px', minHeight: '500px' }}
      />

      <SelectionDialog
        open={drcCodeDialogOpen}
        title="Select DRC Code"
        rows={drcCodesQuery.data?.data ?? []}
        columns={drcCodeColumns}
        onClose={() => {
          setDrcCodeDialogOpen(false);
          setDrcCodePage(0);
        }}
        onSelect={handleSelectDrcCode}
        loading={drcCodesQuery.isLoading}
        searchValue={drcCodeSearchInput}
        onSearchValueChange={setDrcCodeSearchInput}
        onSearch={() => {
          setDrcCodePage(0);
          setDrcCodeSearchTerm(drcCodeSearchInput.trim());
        }}
        onClear={() => {
          setDrcCodeSearchInput('');
          setDrcCodeSearchTerm('');
          setDrcCodePage(0);
          setDrcCodeRowsPerPage(5);
        }}
        totalCount={drcCodesQuery.data?.count ?? 0}
        page={drcCodePage}
        rowsPerPage={drcCodeRowsPerPage}
        onPageChange={setDrcCodePage}
        onRowsPerPageChange={setDrcCodeRowsPerPage}
        paperStyle={{ width: '1573px', minHeight: '500px' }}
      />

      <SelectionDialog
        open={supplyPointDialogOpen}
        title="Select a supply point"
        rows={supplyPointsQuery.data?.data ?? []}
        columns={supplyPointColumns}
        onClose={() => {
          setSupplyPointDialogOpen(false);
          setSupplyPointPage(0);
        }}
        onSelect={handleSelectSupplyPoint}
        loading={supplyPointsQuery.isLoading}
        searchValue={supplyPointSearchInput}
        onSearchValueChange={setSupplyPointSearchInput}
        onSearch={() => {
          setSupplyPointPage(0);
          setSupplyPointSearchTerm(supplyPointSearchInput.trim());
        }}
        onClear={() => {
          setSupplyPointSearchInput('');
          setSupplyPointSearchTerm('');
          setSupplyPointPage(0);
          setSupplyPointRowsPerPage(5);
        }}
        totalCount={supplyPointsQuery.data?.count ?? 0}
        page={supplyPointPage}
        rowsPerPage={supplyPointRowsPerPage}
        onPageChange={setSupplyPointPage}
        onRowsPerPageChange={setSupplyPointRowsPerPage}
        paperStyle={{ width: '1573px', minHeight: '700px' }}
      />

      <SelectionDialog
        open={routeDialogOpen}
        title="Select a route"
        rows={routesQuery.data?.data ?? []}
        columns={routeColumns}
        onClose={() => {
          setRouteDialogOpen(false);
          setRoutePage(0);
        }}
        onSelect={handleSelectRoute}
        loading={routesQuery.isLoading}
        searchValue={routeSearchInput}
        onSearchValueChange={setRouteSearchInput}
        onSearch={() => {
          setRoutePage(0);
          setRouteSearchTerm(routeSearchInput.trim().toLowerCase());
        }}
        onClear={() => {
          setRouteSearchInput('');
          setRouteSearchTerm('');
          setRoutePage(0);
          setRouteRowsPerPage(5);
        }}
        totalCount={routesQuery.data?.count ?? 0}
        page={routePage}
        rowsPerPage={routeRowsPerPage}
        onPageChange={setRoutePage}
        onRowsPerPageChange={setRouteRowsPerPage}
        paperStyle={{ width: '50vw', minWidth: '900px', minHeight: '550px' }}
      />

      <Dialog open={containerTypeDialogOpen} onClose={() => setContainerTypeDialogOpen(false)} maxWidth="md" fullWidth sx={{ '& .MuiDialog-paper': { minHeight: '500px', minWidth: '900px' } }}>
        <DialogTitle>Select a container type</DialogTitle>
        <DialogContent>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Code</TableCell>
                <TableCell>Description</TableCell>
                <TableCell align="right">Action</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {(containerTypesQuery.data ?? []).map((containerType) => (
                <TableRow key={containerType.code} hover>
                  <TableCell>{containerType.code}</TableCell>
                  <TableCell>{containerType.description}</TableCell>
                  <TableCell align="right">
                    <Button size="small" variant="outlined" onClick={() => handleSelectContainerType(containerType)}>
                      Select
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setContainerTypeDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      <DiscardOrderDialog
        open={discardDialogOpen}
        type={isNewOrder ? 'newOrder' : 'existingOrder'}
        onCancel={() => setDiscardDialogOpen(false)}
        onDiscard={() => {
          setDiscardDialogOpen(false);
          onBack?.();
        }}
      />

      <ClearProductsDialog
        open={clearProductsDialogOpen}
        onCancel={handleCancelClearProducts}
        onContinue={handleContinueClearProducts}
      />
      </LocalizationProvider>
      </ThemeProvider>
    </FormProvider>
  );
};
